// Percorso file: \src\pages\UsersList.jsx

// ⛔️ F5: `useMemo` non è più fra gli import — l'unico memo della pagina era
// `squadreEsistenti`, caduto con il contatore «TD».
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/api';
import UserPaymentsModal from '../components/UserPaymentsModal';
import { exportFeesToCSV } from '../utils/exportCsv';
// S5 (veste grafica, doc 07 §8.1 «Aggiunta 26 agosto 2026»): il telaio comune
// e i componenti condivisi degli strumenti admin — gli stessi di Team
// Assignment e Manage User Contexts, riusati e non ricopiati.
import HomeShell from '../components/HomeShell';
// La BARRA DI STATO DELLA STAGIONE (doc 07 §8.2), componente condiviso: dice
// come sta il CLUB — i due anni, la fase, le iscrizioni. Non è il selettore
// «Season Viewed» qui sotto, che dice tutt'altro (quale annata questa pagina
// sta guardando). Legge da sé la sede condivisa (AuthContext): questa pagina
// non le passa nulla, perché la stagione non la comanda.
import SeasonStatusBar from '../components/SeasonStatusBar';
import { PastigliaCategoriaSeNota } from '../components/PastigliaCategoria';
import { categoriaAnagrafica, genereCanonico } from '../utils/categoria';
// ⚠️ L'ORDINE DEI DUE FOGLI CONTA, ed è questo di proposito: il foglio
// CONDIVISO prima, quello della pagina dopo. Così, dove due regole pesassero
// uguale, vince quella di pagina — e non l'ordine con cui il bundle emette i
// file. (AdminTools.css entra comunque nel bundle, lo importa anche il
// componente della pastiglia: qui si dichiara, non si aggiunge.)
import '../components/AdminTools.css';
import './UsersList.css';
import { useAuth } from "../context/AuthContext";
import avatarPlaceholder from '../assets/avatar-placeholder.png';
import AuthAvatar from '../components/AuthAvatar';
import {
  hasRole,
  normalizza,
  canonicalRole
} from '../utils/roleUtils';
// LA SEDE UNICA DELLA LETTURA DI SQUADRA E DELL'ANNO (cantiere «lettore
// unico»): qui arrivano, già scritte, le sei funzioni che questa pagina
// teneva in copia locale — `annoDiLista`, `tesseraValida`, `tessereValide`,
// `eTesseraAmministrativa`, `tessereDiRuolo` e il corpo dell'unione di
// `squadreDellaPersona`. I corpi sono quelli di qui, invariati.
// `statoDelSoggetto` (cantiere «User List per anno guardato», YS-13 Punto 3):
// lo stato della persona PER L'ANNO GUARDATO, letto dalle sue tessere di
// quell'anno con la precedenza di Team Assignment. È il metro delle tre
// letture di stato di questa pagina (base della tabella, base delle card,
// riga ambra); `statoDi` — lo `status` della persona — sopravvive come solo
// RIPIEGO di chi non ha tessere dell'anno, cioè gli amministratori.
import {
  squadreDellaTessera,
  squadreDelSoggetto,
  statoDelSoggetto,
  tessereDelSoggetto
} from '../utils/squadre';
import { annoDiLista } from '../utils/stagione';
// LA SEDE UNICA DELLE QUOTE PER ANNATA (PAY-SEASON F2): dove stanno le quote
// di (persona, anno), e quanto è stato versato. La stessa che leggono la
// finestra Payments e l'export CSV — così i tre non possono divergere, che è
// il difetto che questa pagina ha già chiuso per i ruoli, le squadre e l'anno.
import { quoteDellAnnata, totaleVersato } from '../utils/quote';
import { denaroAschermo } from '../utils/denaro';
// F5: l'etichetta a schermo di un ruolo. Stessa sede unica che usa
// TeamAssignment da F3c — `roleLabel(canonicalRole(...))`, non una tabella
// di traduzione propria di questa pagina.
import { roleLabel } from '../auth/access';
import { isPlayFootballEligible } from '../utils/playFootball';

const sortStrings = (a, b) => (a > b ? 1 : a < b ? -1 : 0);

/* ===============================
   STATO DELLA PERSONA — SEDE UNICA DI PAGINA (P8)
=============================== */
// Una sola funzione risponde alla domanda «in che stato è questa persona?»,
// e da qui derivano TUTTE E TRE le risposte che la pagina dava (o non dava)
// in punti diversi: chi compare in tabella, chi conta nelle card, quale riga
// va in ambra. Prima erano due letture scritte separatamente (il filtro crudo
// della tabella e `isActiveMember` delle card) e la terza non esisteva: tre
// letture dello stesso campo sono il difetto che l'audit 11 §6.2 censisce in
// quattro sedi dell'app, e qui ne restava una copia.
//
// ⚠️ DAL CANTIERE «USER LIST PER ANNO GUARDATO» (YS-13 Punto 3) LE TRE
// RISPOSTE NON PARTONO PIÙ DA QUI. `status` è lo specchio dell'anno
// OPERATIVO, e guardando un'altra annata diceva il falso: un rinnovato con
// tessera 2027 ancora PENDING risultava ACTIVE sul 2027, e un archiviato del
// 2026 con tessera 2027 ACTIVE spariva dal 2027. Le tre letture passano ora
// da `statoDellAnno` (dentro il componente) → `statoDelSoggetto`
// (utils/squadre.js): lo stato delle TESSERE DELL'ANNO GUARDATO, con la
// precedenza di Team Assignment. Questa funzione resta come RIPIEGO, e per
// un caso solo: la persona senza tessere dell'anno — gli amministratori, le
// cui cariche non portano l'anno — che entra in lista per eccezione e per
// cui lo `status` della persona è l'unica cosa che ci sia da leggere. Tutto
// ciò che segue su forma canonica, assente e ignoto vale per quel ripiego.
//
// Forma canonica restituita, e nient'altro: "ACTIVE" · "PENDING" ·
// "ARCHIVED" · "" (stato IGNOTO — un refuso, un "SUSPENDED", un valore
// futuro). Lettura tollerante alla grafia (`normalizza`), come ogni
// confronto dopo P0: un "Archived" storico vale ARCHIVED.
//
// ⚠️ Lo status ASSENTE vale ACTIVE, e non "" — è il metro del backend, che
// lo applica prima ancora che il browser veda il dato (`server.js`,
// `status: utente.status || "ACTIVE"`). Da `GET /users` il campo arriva
// quindi sempre valorizzato e questo ramo non si presenta: resta scritto
// perché il giorno in cui la lista arrivasse per un'altra via la pagina non
// cambi comportamento in silenzio. Il vuoto "" è riservato all'ignoto, che è
// un caso diverso e va trattato diversamente (vedi `activeUsers`).
//
// Resta LOCALE a questa pagina, per decisione del capomastro: le altre sedi
// che leggono lo status con criteri divergenti sono fuori dal perimetro di
// P8, e promuovere l'helper a `utils/` senza convertirle sarebbe una sede
// unica che nessun altro usa. Registrato come residuo, non fatto.
// Le sedi, verificate sul vivo (i numeri dell'audit 11 §6.2 sono invecchiati):
//   - `UniformDistribution.jsx` → `user.status === "ACTIVE"`, crudo;
//   - `UserContextManager.jsx` → `String(u.status).toUpperCase() === "ACTIVE"`,
//     tollerante alla grafia ma senza guardia sul nullo (un `undefined`
//     diventa la stringa "UNDEFINED");
//   - `Messaging` — filtro crudo, anticipo di P6.
const statoDi = (u) => {
  const s = normalizza(u?.status);
  if (s === "") return "ACTIVE";
  return (s === "ACTIVE" || s === "PENDING" || s === "ARCHIVED") ? s : "";
};

/* ===============================
   CHI HA UNA QUOTA DA PAGARE (decisione 7 dell'8/8/2026, VINCOLO)
=============================== */
// Governa il ternario della riga: le tre colonne Total Due / Total Paid /
// Balance E il bottone «Payments» — che non ha una condizione propria, sta
// dentro lo stesso ramo (audit 11 §3.1). L'altro ramo è la cella «N/A».
//
// ⚠️ ERA IL REPERTO DEL COLLAUDO DELL'8/8: `["Player","Fan","eSport"]
// .includes(user.role)` — la stessa persona vedeva le colonne e il bottone
// scritta "Player" e la cella «N/A» scritta "PLAYER". Ora la domanda è una
// sola e passa dal gate unico: sei un giocatore? Per ruolo, in qualunque
// grafia (PLAYER/Player/player), col vocabolario di P0 (Child = PLAYER),
// oppure per context PLAYER attivo — come già conta la card Players.
//
// ⚠️ PREZZO DICHIARATO (decisione del capomastro del 9/8, opzione A): i due
// ruoli storici "Fan" ed "eSport" cadono dall'elenco e le loro righe passano
// a «N/A». Erano ruoli riservati e non offerti già nel modello nuovo
// (access.js: nessuna home, fuori da ROLE_ROUTES) e non sono registrabili dal
// backend (elenco chiuso del 6/8, server.js). Se in beta o in produzione
// esistono documenti così, i loro pagamenti non si aprono più da questa
// pagina: da verificare al collaudo, ed è un cambiamento VOLUTO, non una
// svista. La catena del bottone (modale, handleUpdateFees, PUT /fees) non è
// toccata da P3: cambia solo chi la vede. (⚠️ La catena è invece cambiata in
// PAY-SEASON F2, e non per mano di P3: `handleUpdateFees` non scrive più —
// il `PUT /fees` lo fa la sola finestra, e per l'annata guardata.)
// ⚠️ UL-TESSERE: la domanda si pone ora sulla PERSONA PROIETTATA
// SULL'ANNATA — le sole tessere valide (più il cappello admin), non tutte le
// tessere attive di qualunque anno. Un giocatore del 2026, guardato nel 2027,
// non ha una quota da pagare per il 2027 e la sua riga mostra «N/A».
// Il gate resta UNO (`hasRole` → `canAccess`, auth/access.js): cambia ciò che
// gli si dà in pasto, non la sua logica. `personaDellAnnata` è dichiarata più
// sotto in questo stesso modulo e si legge a runtime, quando la pagina chiama.
const haQuota = (user, anno) => hasRole(personaDellAnnata(user, anno), "PLAYER");

// ===============================
// CONTEXT DISPLAY HELPERS
// ===============================

/* ===============================
   LE TESSERE CHE QUESTA PAGINA LEGGE (UL-TESSERE)
=============================== */
// ⚠️ QUI È IL CUORE DELLA REGOLA NUOVA, e prende il posto di
// `getActiveContexts`, che filtrava il solo status.
//
// ERA: «tessera attiva» voleva dire status ACTIVE, e basta. La pagina
// sceglieva le PERSONE per annata (`season`/`seasons[]` del documento) e poi
// mostrava di ciascuna TUTTE le sue tessere attive, di qualunque anno
// fossero: un coach dell'U12 2026, guardato nel 2027, portava ancora quella
// squadra accanto al proprio nome.
//
// ORA: si mostrano le tessere ACTIVE il cui `cardSeason` vale l'annata che la
// pagina sta GUARDANDO. Da questa lettura discende tutto il resto — chi
// compare in tabella, che cosa dicono Role e Team, che cosa contano le card,
// che cosa filtrano i filtri.
//
// ⚠️ IL PREDICATO NON VIVE PIÙ QUI. `tesseraValida` e `tessereValide` sono
// salite a `utils/squadre.js` — `tesseraDellAnno` e `tessereDelSoggetto` —
// con il corpo di questa pagina, invariato. Erano quattro copie identiche
// (questa, la scheda socio, le due home) più due varianti dichiarate
// (TeamAssignment, UserContextManager): il cantiere «lettore unico» le ha
// riunite senza riscriverne una riga. Questa pagina le chiama con le opzioni
// PREDEFINITE, che sono esattamente ciò che il predicato faceva qui:
//
//   `stati: ["ACTIVE"]`      lo status si legge con `normalizza`, come prima:
//     è il metro del backend (server.js) e la difesa introdotta da P0 in
//     access.js. Un context salvato `status: "Active"` (grafia storica)
//     conta. Nessun default: status assente ≠ ACTIVE — e dalla proiezione di
//     lista lo status arriva SEMPRE, con ripiego a stringa vuota, che ACTIVE
//     non è.
//
//   `senzaAnno: "escludi"`   `cardSeason` MANCA, non vale `null`. La
//     proiezione di lista lo emette con uno spread condizionale
//     (server.js — «esce SE c'e' sul documento, e basta»), quindi una
//     tessera che non ce l'ha arriva SENZA LA CHIAVE. Si verifica perciò la
//     PRESENZA DELLA CHIAVE, non un confronto a `null`: sono due domande
//     diverse, e `ctx.cardSeason === null` non sarebbe mai vero.
//
// ⚠️ L'ANNO ARRIVA NON NORMALIZZATO. La stessa proiezione trasporta il valore
// tale e quale, senza passare da `annoTollerante`: sui documenti storici può
// essere la stringa "2026" invece del numero 2026. Il confronto passa quindi
// da `annoDiLista` (ora in `utils/stagione.js`), lo stesso metro con cui
// questa pagina legge già ogni altro anno: «2027» e 2027 sono lo stesso anno.
// Un `===` crudo fra tipi diversi farebbe sparire in silenzio le tessere
// storiche.
//
// ⚠️ ANNATA IGNOTA (`anno === null`) → LA REGOLA DELL'ANNO NON SI APPLICA, e
// restano le sole ACTIVE. È la dottrina già scritta in questa pagina, ed è la
// stessa guardia sul nullo del cancello dell'anno in AuthContext: su un dato
// che non si sa leggere non si inventa nulla. Qui inventare vorrebbe dire
// svuotare l'intera tabella nei millisecondi dell'apertura, o dopo una
// lettura della stagione fallita.

/* ===============================
   L'ECCEZIONE ADMIN / SUPERADMIN (opzione X)
=============================== */
// ⚠️ NON PUÒ POGGIARE SULL'ANNO, ed è un fatto del dato, non una comodità: le
// tessere amministrative «non portano `cardSeason` e non possono portarlo»
// (AuthContext.js, cancello dell'anno). Cercare «una tessera ADMIN valida per
// l'annata» non ne troverebbe MAI una, e il primo effetto della regola nuova
// sarebbe far sparire dall'elenco gli amministratori del club — cioè proprio
// le persone che questa pagina governa.
//
// L'aggancio è quindi il GATE UNICO, interrogato sulla persona INTERA:
// `hasRole` → `canAccess` → `getActiveRoles` (auth/access.js), che riconosce
// l'admin sia per `role` di primo livello sia per tessera ADMIN attiva, in
// qualunque grafia. Nessun secondo gate scritto qui (CLAUDE.md §2).
const eAmministratore = (user) => hasRole(user, "ADMIN", "SUPERADMIN");

// La tessera amministrativa — quella il cui RUOLO è ADMIN o SUPERADMIN, in
// forma canonica — non si riconosce più qui: `eTesseraAmministrativa` vive in
// `utils/squadre.js`, con il corpo di questa pagina, e la usa l'opzione
// `includiAmministrative` del modulo.

// LE TESSERE DA CUI SI LEGGONO I RUOLI = le valide dell'annata, PIÙ le
// tessere amministrative attive. È l'opzione `includiAmministrative: true`
// del modulo, e questa pagina la chiede nei due soli punti che leggono RUOLI:
// `personaDellAnnata` (qui sotto) e `getDisplayRoles`.
//
// ⚠️ SONO DUE INSIEMI DIVERSI, DI PROPOSITO, e la differenza è tutta
// l'eccezione:
//   - senza l'opzione → le SQUADRE (colonna Team, tendina, filtro,
//                        ordinamento). L'amministratore non porta qui alcuna
//                        voce: una tessera amministrativa non è una squadra, e
//                        includerla scriverebbe «Admin: no team» accanto al
//                        nome di ogni amministratore del club.
//   - con l'opzione   → i RUOLI (colonna Role, filtro Role, card che contano
//                        per ruolo). Qui il cappello admin c'è SEMPRE, anche
//                        nell'annata in cui la persona non ha altre tessere.
//
// ⚠️ L'ECCEZIONE NON ALLARGA LE ALTRE TESSERE DELL'ADMIN. Un ADMIN che è
// anche Coach 2026, guardato nel 2027, ha qui la sola tessera amministrativa:
// la sua tessera Coach 2026 non è valida per il 2027 e non entra né in questo
// elenco né nelle squadre. Compare, dice «Admin», e la cella Team è vuota.
// LA PERSONA COME LA VEDE L'ANNATA GUARDATA: lo stesso documento, con le sole
// tessere di ruolo al posto di `contexts`.
//
// ⚠️ SERVE A INTERROGARE IL GATE UNICO SENZA RISCRIVERLO. `getActiveRoles`
// (auth/access.js) non ha, e non deve avere, un parametro d'anno: qui non se
// ne scrive una seconda copia annuale — si pone la STESSA domanda di prima a
// una persona proiettata sull'annata. `hasRole(personaDellAnnata(u, anno),
// "PLAYER")` è il gate unico, con in pasto le tessere che l'annata ammette.
//
// ⚠️ IL `role` DI PRIMO LIVELLO NON È UNA TESSERA e resta intatto: la regola
// parla delle tessere, e quel campo un anno non lo porta (come le tessere
// amministrative). Toglierlo sarebbe far sparire un dato che la regola non
// nomina, e questa pagina non fa sparire ciò che non sa. Il caso è registrato
// nel rapporto della fetta fra quelli NON coperti dalla regola.
const personaDellAnnata = (user, anno) => ({
  ...user,
  contexts: tessereDelSoggetto(user?.contexts, anno, {
    includiAmministrative: true,
  }),
});

/* ===============================
   STAGIONE — SEDE UNICA DI PAGINA (S4)
=============================== */
// ⚠️ UL-TESSERE: di questo blocco restava la sola `annoDiLista`, il metro con
// cui questa pagina legge un anno in qualunque grafia. `appartieneAllaStagione`
// — che rispondeva a «questa PERSONA è dell'annata X?» — non c'è più: la
// domanda si pone ora sulle TESSERE (`tesseraDellAnno` del modulo), e
// `annoDiLista` la serve identica su tutti e due i lati.
//
// ⚠️ ORA `annoDiLista` NON VIVE PIÙ NEMMENO QUI: è salita a
// `utils/stagione.js`, con il corpo di questa pagina, invariato. Era una
// delle SETTE copie identiche sparse nel frontend, ognuna delle quali
// dichiarava per iscritto di essere una copia. Si importa in testa al file;
// i suoi lettori di pagina (qui sotto `annateDeiDati`, gli alert e le spunte
// Play Football) non cambiano di un carattere.
//
// ⚠️ SI LEGGE, NON SI SCRIVE. Il dato arriva già pronto dalla proiezione di
// lista del backend — `season` (S2), `seasons[]` e `renewedSeason` (S3) — e
// questa fetta si limita a filtrarlo nel browser: nessuna chiamata nuova a
// `GET /users`, nessun accesso alla sotto-raccolta `users/{id}/seasons/{anno}`,
// nessuna scrittura di alcun genere. È il senso della «chiave di lista
// leggera» decisa a diario il 25/8: il filtro dell'elenco si regge su ciò che
// il documento persona porta con sé, senza aprire il cassetto d'annata.
//
// ⚠️ `renewedSeason` NON entra qui, di proposito. Dice «per quale annata ha
// rinnovato», non «di quale annata è socio»: sono due significati che S3 ha
// tenuto separati apposta (S3-CONSEGNA §2.3), e confonderli in un filtro
// vorrebbe dire far sparire dall'elenco chi è socio dell'annata ma il rinnovo
// non l'ha ancora completato. Resta a disposizione del cantiere del rinnovo.

/* ===============================
   ALERT E PLAY FOOTBALL PER-STAGIONE (PFA) — sede unica di lettura di pagina
=============================== */
// I due dati sono per-stagione SULLA PERSONA, non in tessera: il backend li
// porta in proiezione come `alertSeasons` (elenco di annate) e
// `playFootballSeasons` (mappa per annata dei due booleani). Qui si risponde
// alle sole due domande che la pagina pone, e si risponde in un posto solo.
//
// ⚠️ UNA FUNZIONE SOLA, INTERROGATA PIÙ VOLTE, e non la stessa condizione
// ricopiata a ogni punto di resa: è la dottrina che questa pagina ha già
// scritto sul pallino Alert («una copia può divergere da ciò che `toggleAlert`
// consente davvero»). Il segno e il comando devono leggere lo stesso criterio.
//
// ⚠️ ANNATA IGNOTA (`anno === null`) → NESSUNA RISPOSTA, cioè nessun alert e
// nessuna spunta. Non è una scelta di comodo: senza un anno la domanda «questa
// persona ha l'alert NELL'ANNO?» non ha oggetto, e inventarne uno vorrebbe
// dire marcare (o smarcare) persone in un'annata che nessuno ha scelto. È lo
// stesso verso già scritto in questa pagina — «su un dato che non si sa
// leggere non si inventa nulla» — e vale anche per i due comandi, che in
// quello stato non partono (vedi `toggleAlert` e `togglePF`).
//
// ⚠️ NESSUN RIPIEGO SUL CAMPO SENZ'ANNO. `user.alertFlag` e `user.playFootball`
// arrivano ancora in proiezione, ma qui NON si leggono: un'annata senza valore
// è un'annata senza valore, non un'annata che eredita il dato vecchio.

// L'annata guardata è fra quelle in cui la persona ha un alert?
// Il confronto passa da `annoDiLista` su ciascuna voce, lo stesso metro con
// cui la pagina legge ogni altro anno: «2027» e 2027 sono lo stesso anno.
const haAlertNellAnnata = (user, anno) => {
  if (anno === null) return false;
  if (!Array.isArray(user?.alertSeasons)) return false;
  return user.alertSeasons.some((voce) => annoDiLista(voce) === anno);
};

// I due valori di Play Football dell'annata guardata. Sempre un oggetto: se
// l'annata non si sa, se la mappa non c'è o se quell'anno non ha voce, i due
// booleani valgono `false` — che è «adempimento non ancora fatto», e non
// «dato che non c'è», perché in questa colonna le due cose si dicono uguali
// da sempre (una spunta spenta).
// La chiave si cerca con `annoDiLista`, non con un `[String(anno)]` crudo: il
// backend normalizza le chiavi, ma il metro della pagina resta il suo.
const playFootballDellAnnata = (user, anno) => {
  const vuoto = { registered: false, feePaidByClub: false };

  if (anno === null) return vuoto;

  const mappa = user?.playFootballSeasons;

  if (!mappa || typeof mappa !== "object" || Array.isArray(mappa)) return vuoto;

  const chiave = Object.keys(mappa).find((k) => annoDiLista(k) === anno);

  if (chiave === undefined) return vuoto;

  return {
    registered: Boolean(mappa[chiave]?.registered),
    feePaidByClub: Boolean(mappa[chiave]?.feePaidByClub)
  };
};

/*
 * ⛔️ RIMOSSA in UL-TESSERE — `appartieneAllaStagione(utente, anno)`.
 *
 * Rispondeva a «questa PERSONA è dell'annata X?» leggendo `seasons[]` e
 * `season` del documento, ed era il criterio con cui nasceva la coorte. La
 * regola nuova cambia SORGENTE: l'annata non la dice più la persona, la
 * dicono le sue TESSERE (`cardSeason`), e chi compare è chi ha almeno una
 * tessera valida — più gli amministratori, per eccezione. Vedi
 * `coorteDiStagione`.
 *
 * ⚠️ `season` e `seasons[]` NON sono stati rimossi dal dato né dalla pagina:
 * restano la sorgente di `annateDeiDati`, cioè delle annate offerte dalla
 * tendina «Season Viewed». Cade la funzione, non i campi.
 *
 * ⚠️ `annoDiLista` resta, e ora ha due lettori: `annateDeiDati` (che legge
 * gli anni della PERSONA) e `tesseraDellAnno` del modulo (che legge l'anno
 * della TESSERA).
 * È lo stesso metro di grafia per entrambi, ed è il motivo per cui «2027» e
 * 2027 restano lo stesso anno su tutti e due i lati.
 *
 * ⚠️ LA TOLLERANZA SULL'ANNATA IGNOTA NON È CADUTA CON LA FUNZIONE, si è
 * spostata: `anno === null` la trattava qui facendo passare tutte le
 * PERSONE, e la tratta ora in `tesseraDellAnno` facendo passare tutte le
 * tessere ACTIVE. È lo stesso momento e la stessa dottrina — i primi istanti
 * dopo l'apertura, o una lettura della stagione fallita senza nemmeno
 * un'annata nei dati su cui ripiegare: su un dato che non si sa leggere non
 * si inventa nulla.
 *
 * ⚠️ LA DIFFERENZA, DETTA PER INTERO: la tolleranza vecchia lasciava passare
 * OGNI persona, la nuova ogni TESSERA ATTIVA. Con l'annata ignota, chi non ha
 * alcuna tessera attiva e non è amministratore ora non compare, dove prima
 * compariva con Role e Team vuoti. È il caso U3, e vale in ogni annata: la
 * regola nuova non lo prevede più in elenco.
 */

/**
 * Etichetta a schermo di un ruolo, dal ruolo CANONICO.
 *
 * ⚠️ F5: è la stessa `etichettaRuolo` che TeamAssignment ha adottato in F3c,
 * e per lo stesso motivo — «Manager», «TD», «ManagerTD» sono tutti un COACH
 * (CLAUDE.md §2), e le due pagine non devono chiamare in due modi diversi la
 * stessa tessera. La tabella delle etichette resta una sola, in
 * `auth/access.js`: qui non se ne scrive una seconda.
 *
 * Chiude in questa pagina il rinvio annotato prima («la resa non si traduce,
 * materia di P4/U1-G»): il rinvio serviva a non tradurre di straforo dentro
 * un CONFRONTO, mentre qui la traduzione è il fine dichiarato dell'incarico.
 */
const etichettaRuolo = (ruolo) => roleLabel(canonicalRole(ruolo));

// costruisce righe ROLE multiple (base + contexts)
const getDisplayRoles = (user, anno) => {
  const roles = [];

  // ruolo base
  if (user.role) {
    roles.push(etichettaRuolo(user.role));
  }

  // Ruoli dalle TESSERE.
  //
  // ⚠️ F5 — LA SPIA «TEAMTD» NON SI LEGGE PIÙ. Prima, una tessera il cui
  // campo `team` valeva "TEAMTD" faceva scrivere «TEAMTD (Manager)»: cioè si
  // deduceva un RUOLO da una SQUADRA, per giunta da un valore che nessun
  // percorso vivo scrive più. L'allenatore ora si riconosce da ciò che la
  // tessera dice di essere — il suo `role`, in forma canonica — esattamente
  // come fa TeamAssignment da F3c. La riga di quella tessera è quindi il suo
  // ruolo, come per ogni altra: nessun ramo speciale, nessuna spia.
  // Cade con essa l'asimmetria lasciata a verbale da P7 (colonna Role che
  // scriveva «TEAMTD (…)» senza guardare il ruolo, colonna Team che invece
  // il ruolo lo pretendeva): le due colonne ora leggono lo stesso dato.
  //
  // ⚠️ SI CONFRONTA IN FORMA CANONICA. Il confronto `ctx.role !== user.role`
  // metteva a paragone due grafie qualsiasi: un documento `role: "Player"`
  // con un context `role: "PLAYER"` faceva leggere «Player · PLAYER», cioè
  // lo stesso ruolo due volte, e un "Manager" con context "COACH" altrettanto.
  // Col canonico il doppione sparisce; restano i ruoli davvero diversi.
  //
  // ⚠️ Questa colonna elenca RUOLI, non tessere: due tessere dello stesso
  // ruolo canonico danno una voce sola, e va bene qui perché la voce è il
  // ruolo. La colonna TEAM fa il contrario — là ogni tessera ha la sua voce,
  // perché ogni tessera ha le SUE squadre (è il baco che il modello B chiude).
  // ⚠️ UL-TESSERE: le tessere DI RUOLO dell'annata guardata — le valide, più
  // il cappello amministrativo che anno non ne porta. Prima erano tutte le
  // tessere attive, di qualunque anno: un coach del 2026 restava «Coach»
  // anche guardando il 2027.
  tessereDelSoggetto(user?.contexts, anno, {
    includiAmministrative: true,
  }).forEach(ctx => {
    if (!ctx.role) return;
    if (canonicalRole(ctx.role) === canonicalRole(user.role)) return;

    const etichetta = etichettaRuolo(ctx.role);
    if (!roles.includes(etichetta)) {
      roles.push(etichetta);
    }
  });

  return roles;
};

// costruisce info TEAM compatta — UNA VOCE PER TESSERA (F5)
//
// ⚠️ QUESTA COLONNA NON LEGGE PIÙ `user.team`. Il campo di primo livello è
// una squadra sola per PERSONA: la stessa riga sia che la persona giochi
// nell'U12, sia che alleni l'U10. È il baco che il modello B chiude, e
// mostrarlo qui lo faceva sopravvivere a schermo anche dopo che le
// scritture erano state spostate sulle tessere (F3a/F3b).
// Il CAMPO resta — sui documenti e nella proiezione — finché le schermate
// ancora da migrare non l'hanno lasciato: si toglie in pulizia finale, non
// qui. Ciò che cade in F5 è la sua LETTURA in questa colonna.
//
// ⚠️ NIENTE FUSIONI. Ogni tessera ATTIVA ha la sua voce, anche quando due
// tessere hanno lo stesso ruolo canonico: sono due tessere, e ognuna ha le
// SUE squadre. Fonderle rifarebbe, in resa, l'errore che il modello vieta
// nel dato. (La colonna ROLE fa il contrario, e per una ragione sua: là la
// voce è il ruolo, e i ruoli uguali sono uno solo.)
//
// ⚠️ SI LEGGONO I CAMPI CHE LA PROIEZIONE GIÀ MANDA. `GET /users` proietta
// ogni tessera passando da `squadreDellaTessera` (server.js), quindi la
// coppia `{ teams, allTeams }` del modello B arriva già formata anche per
// le tessere non ancora migrate. Qui NON si riapplica quella regola: si
// legge, con le sole guardie di tipo che un render deve avere per non
// sollevare. Una seconda copia della regola B in questa pagina sarebbe
// esattamente il genere di doppione che F2/F3 hanno tolto di mezzo.
//
// ⚠️ La coppia si guarda INSIEME: con `allTeams` vero l'elenco non si
// mostra (è vuoto per costruzione, e un numero — o un nome — direbbe il
// falso dove il permesso è TUTTE).
//
// Cade con il conteggio anche l'elenco vero delle squadre del club
// (`squadreEsistenti`, P7): serviva a non contare come squadra una voce che
// squadra non era. Qui non si conta più nulla — si scrivono i NOMI che la
// tessera porta — quindi quel presidio non ha più materia.
// ⚠️ LE TESSERE PARENT NON HANNO VOCE IN QUESTA COLONNA (F9a). Non è una
// tessera senza squadre — quella la voce ce l'ha, e dice «no team»: è un
// ruolo che squadre non ne prende affatto. Le squadre di una famiglia sono
// quelle dei FIGLI e stanno sulle loro tessere PLAYER; scrivere «Parent: no
// team» accanto al nome di un genitore annunciava una mancanza dove non c'è
// nulla che manchi.
// È per TESSERA, come tutto il resto della colonna: una persona
// Player + Parent mostra la voce del Player e nient'altro; una persona che ha
// la sola tessera Parent ha la cella VUOTA (l'elenco resta vuoto, e la cella
// non stampa nulla).
// Il ruolo si guarda con `canonicalRole`, la stessa lente dell'etichetta qui
// sotto e del resto della pagina: nel vocabolario nessun ruolo storico
// diventa PARENT, quindi qui cade esattamente ciò che PARENT è scritto.
// ⛔️ SOLO RESA: il filtro, la tendina e l'ordinamento della colonna (F5)
// leggono altrove (`squadreDellaPersona`) e NON sono stati toccati.
// ⚠️ LA GIUSTIFICAZIONE SCRITTA QUI ERA FALSA e F9b la corregge: diceva che
// «una tessera PARENT non porta nomi di squadra, quindi non aveva peso
// nemmeno lì». Non è vero nei DATI. Fino a F9a il controllo delle squadre in
// Team Assignment era offerto anche sulle righe PARENT: una tessera PARENT
// storica PUÒ avere squadre scritte dentro, ed è appunto perché poteva
// averle che F9a ha nascosto quel controllo. `squadreDellaPersona` legge
// TUTTE le tessere attive senza guardare il ruolo, quindi quelle squadre
// residue contano ancora nel filtro, nella tendina e nell'ordinamento.
// La differenza vera è un'altra, e regge: quei lettori NON SONO STATI
// TOCCATI, quindi la loro resa è identica a prima di F9a. La ripulitura del
// dato — togliere le squadre dalle tessere PARENT esistenti — è materia del
// BACKFILL, non di una fetta di resa.
const getTeamInfo = (user, anno) => {
  // ⚠️ UL-TESSERE: le tessere VALIDE dell'annata, non tutte le attive. E non
  // le tessere di RUOLO (`includiAmministrative`): la tessera amministrativa
  // non è una squadra, e passarla di qui scriverebbe una voce «no team»
  // accanto a ogni amministratore.
  return tessereDelSoggetto(user?.contexts, anno)
    .filter((ctx) => canonicalRole(ctx.role) !== "PARENT")
    .map((ctx) => {
      // ⚠️ UL-VESTE (9/9/2026): LA VOCE NON PORTA PIÙ IL PREFISSO DI RUOLO.
      // Fino a qui ogni voce era «Player: u9s» — il ruolo CANONICO della
      // tessera, poi le sue squadre. Il committente ha deciso che la cella
      // mostri le SOLE squadre: «u9s». La decisione è sua ed è consapevole:
      // la ricognizione UL-VESTE aveva messo a verbale che, con due o più
      // voci, il prefisso era l'unico segno che diceva DI CHI fosse ciascuna
      // squadra, e che la colonna Role NON lo ricostruisce (le due liste non
      // sono parallele: origini, esclusioni e deduplica sono diverse).
      // Resta a verbale, non a codice.
      // Di conseguenza `etichettaRuolo` non serve più QUI; resta in uso in
      // `getDisplayRoles` (colonna Role), che questa fetta non tocca.

      // ⚠️ LA COPPIA SI LEGGE DAL MODULO, e il FILTRO DEI NOMI VUOTI RESTA
      // QUI. `squadreDellaTessera` non filtra le voci vuote nel suo ramo
      // canonico — è così in tutte le copie che ha sostituito — e spostare
      // il filtro dentro di lei cambierebbe l'esito di quel ramo in
      // silenzio. Il filtro sta dove stava: nel chiamante.
      const { teams, allTeams } = squadreDellaTessera(ctx);

      if (allTeams) {
        return "all teams";
      }

      const squadre = teams.filter((nome) => normalizza(nome) !== "");

      // Nessuna squadra NON è una tessera da nascondere: la voce resta e lo
      // dice. È la stessa scelta della vecchia riga «TD» senza numero — in
      // questa colonna la voce è anche ciò che dichiara che la persona ha
      // quella tessera.
      return squadre.length > 0
        ? squadre.join(", ")
        : "no team";
    });
};

/* ===============================
   LE SQUADRE DI UNA PERSONA — SEDE UNICA DEI COMANDI DI SQUADRA
   (completamento F5)
=============================== */
// I tre comandi di squadra della pagina — la TENDINA di filtro, il FILTRO e
// l'ORDINAMENTO della colonna Team — leggevano ancora `user.team`, cioè il
// campo di primo livello che F5 ha smesso di MOSTRARE (e che nessuna
// scrittura viva aggiorna più da F3b). Restava così una pagina che filtrava e
// ordinava su un valore che nessuna cella scrive: un coach dell'U12 che ha
// quella squadra solo in tessera non compariva filtrando per «U12», e
// l'ordinamento raggruppava le righe per una squadra invisibile.
//
// ⚠️ NON è una seconda copia della regola del modello B. La coppia
// `{ teams, allTeams }` arriva GIÀ FORMATA dalla proiezione di `GET /users`
// (server.js, `squadreDellaTessera`), anche per le tessere non ancora
// migrate: qui si LEGGE, con le sole guardie di tipo, esattamente come fa la
// colonna in `getTeamInfo`. Le sentinelle storiche — «ALL» in `approvedTeams`,
// «TEAMTD» in `team` — non arrivano mai fin qui: la proiezione ha già
// tradotto la prima in `allTeams:true` e la seconda non è mai stata un nome
// di squadra. Nessun elenco nero di valori-spia va quindi scritto in questa
// pagina; scriverlo sarebbe la copia locale che F2/F3 hanno tolto di mezzo.
//
// ⚠️ La coppia si guarda INSIEME, come nella colonna: con `allTeams` vero
// l'elenco è vuoto per costruzione, e la persona vale «tutte le squadre».
//
// ⚠️ Qui le tessere si SOMMANO, mentre la colonna le tiene separate: là la
// voce è la tessera (e ogni tessera ha le SUE squadre), qui la domanda è
// «questa persona sta in quella squadra?», e una persona può giocare
// nell'U12 e allenare l'U10 — due tessere, due squadre, ed è proprio il caso
// che il campo `user.team` non sapeva dire.
//
// Restituisce `{ nomi, tutte }`: i NOMI VERI di squadra, senza doppioni
// (confronto normalizzato, grafia della prima occorrenza), e il permesso
// «tutte le squadre».
// ⚠️ NON C'È PIÙ UN CORPO QUI DENTRO: l'unione la fa `squadreDelSoggetto`
// (utils/squadre.js), che è lo stesso corpo di prima. Resta il NOME, e con
// esso le DUE OPZIONI che questa pagina sceglie diversamente dalle altre —
// scritte una volta sola, perché ripeterle nei tre comandi che le usano
// sarebbe la copia locale che questo cantiere sta togliendo:
//
//   `assorbeTutte: false` — «tutte le squadre» NON svuota l'elenco dei nomi.
//     La scheda socio e le due home fanno il contrario (là il permesso si
//     rende a parole, «All teams», e i nomi non servono più). Qui i nomi
//     servono eccome: la TENDINA li raccoglie da tutte le persone, e
//     svuotare l'elenco di un coach «tutte le squadre» che è anche giocatore
//     dell'U12 toglierebbe «U12» dalla tendina di tutti.
//
//   `doppioni: "normalizzati"` — «U12» e «u12» sono una voce sola, non due,
//     e a schermo va la prima grafia incontrata. È la regola che la tendina
//     dichiara più sotto, ed è più stretta di quella della scheda socio.
//
// ⚠️ UL-TESSERE: le tessere sono le VALIDE dell'annata guardata (le opzioni
// predefinite del modulo: solo ACTIVE, tessera senz'anno esclusa, nessuna
// amministrativa). La squadra di un'altra annata non è una squadra di
// questa, né per il filtro né per la tendina né per l'ordinamento.
const squadreDellaPersona = (user, anno) =>
  squadreDelSoggetto(user?.contexts, anno, {
    assorbeTutte: false,
    doppioni: "normalizzati"
  });

// IL FILTRO per squadra: corrisponde chi ha quella squadra in ALMENO UNA
// tessera attiva. Confronto normalizzato (`normalizza`), come ogni confronto
// di questa pagina dopo P0: «U12», «u12» e « U12 » sono la stessa squadra, e
// i documenti storici contengono tutte e tre le grafie.
//
// ⚠️ Il coach «tutte le squadre» (`allTeams`) CORRISPONDE A QUALUNQUE filtro
// di squadra, e non per comodità: il suo permesso dice che quelle squadre
// sono anche le sue: nasconderlo filtrando per U12 darebbe un elenco in
// contraddizione con la sua stessa riga, che a schermo legge «Coach: all
// teams». È lo stesso principio per cui il filtro dei ruoli passa dal gate
// unico invece di confrontare stringhe.
const haLaSquadra = (user, squadra, anno) => {
  const { nomi, tutte } = squadreDellaPersona(user, anno);
  if (tutte) return true;

  const cercata = normalizza(squadra);
  return nomi.some((nome) => normalizza(nome) === cercata);
};

// LA CHIAVE D'ORDINAMENTO della colonna Team.
// Una persona può avere più squadre e un ordinamento ha bisogno di UN valore
// solo: si prende il primo in ordine alfabetico, così la chiave non dipende
// dall'ordine in cui le tessere sono state salvate (che è un caso, non un
// dato). Si confronta in forma normalizzata per la stessa ragione per cui il
// ruolo si ordina in forma canonica (D-E del 9/8): senza, «u12» e «U12»
// finiscono in due blocchi lontani dell'elenco.
//
// ⚠️ `allTeams` prevale sui nomi, per lo stesso metro del filtro: chi vale
// «tutte le squadre» vi corrisponde tutte, e qui sta in un gruppo suo invece
// di essere collocato secondo una tessera che magari non è nemmeno quella
// che allena.
const CHIAVE_TUTTE_LE_SQUADRE = "ALL TEAMS";

const chiaveSquadra = (user, anno) => {
  const { nomi, tutte } = squadreDellaPersona(user, anno);
  if (tutte) return CHIAVE_TUTTE_LE_SQUADRE;

  const ordinati = nomi.map((nome) => normalizza(nome)).sort(sortStrings);
  return ordinati.length > 0 ? ordinati[0] : "";
};

// ⚠️ Chi non ha SQUADRE va in fondo, non in cima. Con `sortStrings` da solo
// la stringa vuota ordina PRIMA di ogni nome, e la prima schermata
// dell'elenco ordinato per Team sarebbe fatta di celle senza squadra — che è
// il contrario di ciò per cui si ordina una colonna. Il verso si inverte con
// la freccia della colonna, come per ogni altro campo.
const confrontaSquadre = (a, b, anno) => {
  const ca = chiaveSquadra(a, anno);
  const cb = chiaveSquadra(b, anno);

  if (ca === cb) return 0;
  if (ca === "") return 1;
  if (cb === "") return -1;
  return sortStrings(ca, cb);
};

const UsersList = () => {
  const [users, setUsers] = useState([]);
  // ⛔️ F5: qui viveva `teams` (l'elenco squadre del club, per il contatore
  // «TD»). Non ha più lettori — vedi la nota su `squadreEsistenti`.
  // ⚠️ S4 leggeva qui il solo utente e si andava a prendere la stagione da
  // sola, con una chiamata propria a `GET /season-config`. Non più: i due
  // anni del club e lo stato della loro lettura vivono nella sede condivisa
  // (AuthContext, YS-5), già normalizzati a intero. Una chiamata di rete in
  // meno a ogni apertura della pagina, e un solo posto che dice l'annata.
  const {
    user: currentUser,
    operatingSeason,
    enrollmentSeason,
    seasonStatus,
  } = useAuth();

  /* ===============================
     I DUE COMANDI DELLA PAGINA — un solo criterio (decisione 1: i comandi
     si danno per PERMESSO, mai per cappello indossato)
  =============================== */
  // Vale per il pallino Alert E per le spunte Play Football, che prima
  // dichiaravano criteri diversi: Alert leggeva `currentUser.role` in
  // minuscolo (cieco ai contexts: un ADMIN per context attivo vedeva il
  // pallino inerte), le spunte PF avevano un `canEditPF = () => true`, cioè
  // nessun criterio affatto — e per giunta chiamato in due punti con un
  // argomento che ignorava.
  //
  // Il criterio ora è il gate unico, lo stesso con cui `RequireRole` apre
  // questa pagina (App.jsx: /users è riservata ad ADMIN e SUPERADMIN):
  // dentro UsersList la costante è quindi sempre vera per chi è entrato, e
  // NESSUNO PERDE UN COMANDO. Lo guadagna invece l'ADMIN o SUPERADMIN per
  // CONTEXT attivo: passava RequireRole ma il pallino Alert gli restava
  // inerte, perché quel controllo leggeva solo `currentUser.role` (le spunte
  // PF gli funzionavano già, ma solo perché `canEditPF` non chiedeva nulla a
  // nessuno). È il difetto che questo blocco chiude, e si vede a schermo.
  // Serve inoltre perché i due comandi dicano la verità su chi li può usare
  // invece di affidarsi al gate della rotta: il giorno in cui la pagina si
  // aprisse a un altro ruolo, Alert e PF non si troverebbero scoperti.
  // (decisione del capomastro del 9/8, D-D)
  const puoAmministrare = hasRole(currentUser, "ADMIN", "SUPERADMIN");
  // Cantiere ADMIN-SA (26/9/2026, decisione del committente): le tre card
  // del denaro (Fees Collected, Outstanding Fees, Average Fee / Player) sono
  // del solo SUPERADMIN; l'ADMIN vede le altre cinque. Stessa forma di
  // UniformDistribution.jsx e TeamAssignment.jsx. Solo vista: i totali si
  // calcolano comunque, e colonne, filtro Balance, Export CSV e finestra
  // Payments restano come sono.
  const soloSuperAdmin = hasRole(currentUser, "SUPERADMIN");
  const [selectedUser, setSelectedUser] = useState(null); // Per pagamenti
  // ⚠️ `selectedProfile` non esiste più (cantiere View Profile, fase 3): la
  // scheda del tesserato non è più una finestra aperta sopra questo elenco,
  // è una PAGINA su rotta propria e «View Profile» ci naviga. Con lo stato
  // cade anche `onUpdate={() => fetchUsers()}`: quando il «Back» riporta
  // qui, questa pagina si rimonta e l'elenco lo rilegge il suo `useEffect`.
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterBalance, setFilterBalance] = useState('all');

  /* ===============================
     L'UNICO STATO DELLA STAGIONE (S4, rifatto nel passo B)
  =============================== */
  // ⛔️ Qui vivevano TRE stati. Due se ne sono andati e non per pulizia
  // formale: `stagioneDelClub` e `cassettinaFallita` erano la cassettina
  // letta da questa pagina per conto proprio (`GET /season-config`) e il
  // segnale che quella lettura era finita male. Entrambi i fatti li dice ora
  // la sede condivisa (AuthContext): `operatingSeason`/`enrollmentSeason` per
  // gli anni, `seasonStatus` per com'è andata. Due stati locali che dicevano
  // ciò che l'app già sapeva erano due copie da tenere d'accordo per sempre.
  //
  // Resta uno stato solo, e risponde all'unica domanda che è di questa
  // pagina e di nessun altro:
  //
  //   `stagioneScelta`    — quale annata l'amministratore ha scelto di
  //                         guardare. Due valori:
  //                           `undefined` = non ha ancora scelto → vale il
  //                                         default calcolato;
  //                           un numero   = quell'annata.
  //
  // ⚠️ Il terzo valore `null` («All seasons») NON esiste più: la voce è stata
  // tolta dalla tendina nel passo B, perché un elenco che conta e mostra
  // insieme annate diverse non dice niente di vero su nessuna. Il selettore
  // mostra sempre e solo UN'annata.
  //
  // ⚠️ `undefined` resta significativo: il default non si assegna mai con un
  // effetto, si CALCOLA (vedi `stagioneAttiva`, più sotto). Una volta che
  // l'amministratore ha toccato la tendina, `stagioneScelta` non è più
  // `undefined` e nulla può più riportarlo sull'annata corrente alle sue
  // spalle — nemmeno una rilettura dell'elenco dopo un comando di riga.
  const [stagioneScelta, setStagioneScelta] = useState(undefined);

  // ⚠️ MEMBER: la chiave di partenza è `member`, non più `firstName`. Le due
  // colonne del nome sono diventate una sola (vedi il `thead`), e con essa
  // una sola chiave e una sola freccia. Il verso di partenza non cambia.
  const [sortBy, setSortBy] = useState('member');
  const [sortDir, setSortDir] = useState('asc');

  /* ===============================
     CIÒ CHE LA PAGINA HA DA DIRE (P9, decisione 12 dell'8/8/2026)
  =============================== */
  // Le tre operazioni che la decisione 12 nomina fallivano in SILENZIO: il
  // caricamento dell'elenco, la spunta Play Football e il pallino Alert.
  // (Una quarta lettura muta esisteva — `fetchTeams` — lasciata fuori dal
  // perimetro di questo punto; da F5 non esiste più, perché la colonna Team
  // non ha più bisogno dell'elenco squadre del club.)
  // Il `console.error` c'era — e resta, è la diagnostica — ma nella console
  // del browser, dove nessun amministratore guarda. A schermo non accadeva
  // nulla: la spunta non cambiava, il pallino nemmeno, e l'elenco vuoto era
  // indistinguibile da «il club non ha soci».
  //
  // ⚠️ `handleUpdateFees` restava fuori: aveva il suo `alert` da prima e non
  // toccava questo stato, quindi un salvataggio quota riuscito non spegneva un
  // messaggio comparso prima. Da PAY-SEASON F2 non è più così, e non perché
  // qualcuno abbia allineato le due sedi: quella funzione non scrive più nulla
  // — è diventata la sola rilettura dell'elenco — e passa quindi da
  // `fetchUsers`, cioè da questo stato. La resa per bene resta materia di
  // U1-G.
  //
  // Forma RATIFICATA dal capomastro (F1, banner di pagina) sul modello già in
  // opera in UserContextManager (P5): stato unico, resa inline e minima sopra
  // la tabella, nessuna classe nuova, resa per bene rimandata a U1-G. Qui è
  // una STRINGA e non `{ tipo, testo }` come là: in quella finestra convivono
  // un errore e un avviso che guasto non è, e i due andavano distinti; queste
  // tre sedi sono tutte e tre guasti.
  //
  // ⚠️ NON si sceglie `alert()` — che la pagina usava in `handleUpdateFees`
  // fino a PAY-SEASON F2, e che oggi resta solo nella finestra Payments, dove
  // le scritture sono rimaste — perché `fetchUsers` parte DA SOLA all'apertura
  // (useEffect qui sotto): un backend giù accoglierebbe l'amministratore con
  // una finestra di sistema modale da chiudere prima di poter fare qualunque
  // cosa. Il banner dice la stessa cosa senza sbarrare la strada.
  //
  // ⚠️ I testi sono in inglese come tutto ciò che è a schermo, e nessuno
  // nomina la causa tecnica: chi legge non deve sapere se è caduto il
  // backend, la rete o il permesso.
  const [messaggio, setMessaggio] = useState(null);

  // ⛔️ F5: qui c'era anche `fetchTeams()`. La pagina non ha più bisogno
  // dell'elenco squadre del club — vedi la nota su `squadreEsistenti` più
  // sotto — quindi la lettura non parte più.
  // ⛔️ PASSO B: la seconda lettura se n'è andata di nuovo, e stavolta per
  // sempre. S4 l'aveva rimessa (`leggiStagioneDelClub`) perché serviva
  // l'annata del club per far partire il selettore dalla stagione giusta:
  // dai soli dati si ricava al più l'annata più recente fra quelle già
  // popolate, che al rollover è ancora quella VECCHIA. Il fatto resta vero;
  // cambia da dove si prende — dalla sede condivisa (AuthContext), che l'ha
  // già letta all'ingresso. All'apertura della pagina parte quindi una sola
  // chiamata di rete, quella dell'elenco.
  useEffect(() => {
    fetchUsers();
  }, []);

  // ⚠️ È anche la RILETTURA dopo le due azioni qui sotto: quando va a buon
  // fine spegne il messaggio, così un guasto vecchio non resta a schermo
  // sopra una tabella che nel frattempo si è aggiornata.
  //
  // ⚠️ Caso limite dichiarato e NON chiuso: due azioni sovrapposte. Se una
  // rilettura in volo torna DOPO che un'altra azione è fallita, il suo
  // `setMessaggio(null)` spegne un errore appena comparso. Le due chiamate
  // qui sotto sono attese (`await`) per igiene — una promessa lasciata
  // correre dentro una funzione `async` è un invito agli errori — ma
  // l'attesa NON chiude la corsa: nessuno aspetta il ritorno di `togglePF` o
  // di `toggleAlert` (sono `onClick`), quindi due comandi premuti a un
  // istante l'uno dall'altro restano sovrapposti esattamente come prima.
  // Chiuderlo per davvero vorrebbe dire numerare le richieste, ed è
  // sproporzionato per un banner la cui resa è già rimandata a U1-G.
  const fetchUsers = async () => {
    try {
      const data = await api.get('/users');
      setUsers(Array.isArray(data) ? data : []);
      setMessaggio(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
      // La tabella vuota da sola direbbe il falso: «nessun socio» invece di
      // «non sono riuscito a leggere». `setUsers([])` resta — è il ripiego
      // che la pagina sa disegnare — ma ora accanto c'è chi lo spiega.
      setMessaggio('Could not load the member list. Please try again.');
    }
  };

  /* ===============================
     LA CASSETTINA DELLA STAGIONE — dove NON si legge più (passo B)
  =============================== */
  // ⛔️ Qui viveva `leggiStagioneDelClub`, la chiamata propria di questa
  // pagina a `GET /season-config`. È stata tolta: la stessa cassettina la
  // legge già AuthContext all'ingresso (YS-5) e la tiene per tutta l'app in
  // `operatingSeason`, `enrollmentSeason` e `seasonStatus`, coi valori già
  // normalizzati a intero. Una pagina che se la rileggeva da sola aggiungeva
  // una chiamata di rete a ogni apertura e una seconda verità da tenere
  // d'accordo con la prima.
  //
  // ⚠️ NON CAMBIA il modo di trattare il guasto: come prima, se la stagione
  // non si sa la pagina resta interamente utilizzabile e nessun banner
  // compare. Cambia solo chi lo dice — `seasonStatus === "error"` al posto
  // dello stato locale `cassettinaFallita` — e il ripiego resta lo stesso
  // (vedi `stagionePredefinita`). La diagnostica in console la fa ora
  // AuthContext, che è dove la lettura avviene.

    // ⚠️ PFA: si scrive SULL'ANNATA CHE LA PAGINA STA GUARDANDO
    // (`stagioneAttiva`, cioè «Season Viewed»), e i due valori si leggono
    // dalla stessa annata — non più dal campo senz'anno. Il valore corrente e
    // quello scritto vengono ora dalla stessa sede: se divergessero, la spunta
    // direbbe una cosa e il comando ne farebbe un'altra.
    //
    // ⚠️ ANNATA IGNOTA → IL COMANDO NON PARTE. Senza un anno la rotta non
    // saprebbe dove scrivere (e risponderebbe 400): meglio non chiedere che
    // marcare un'annata che nessuno ha scelto. È la stessa guardia di
    // `puoAmministrare` qui sopra, su un'altra condizione.
    const togglePF = async (user, field) => {
      if (!puoAmministrare) return;
      if (stagioneAttiva === null) return;

      const corrente = playFootballDellAnnata(user, stagioneAttiva);

      try {
        await api.put(`/users/${user.id}/playfootball`, {
          season: stagioneAttiva,

          registered:
            field === "registered" ? !corrente.registered : corrente.registered,

          feePaidByClub:
            field === "feePaidByClub"
              ? !corrente.feePaidByClub
              : corrente.feePaidByClub
        });
        await fetchUsers();
      } catch (err) {
        console.error("Error updating Play Football flag:", err);
        // La spunta non cambia: senza questo, l'unico segnale sarebbe che
        // non è successo niente.
        setMessaggio('Play Football not updated. Please try again.');
      }
    };

    // ⚠️ PFA: il gesto non è più «imposta un booleano» ma «accendi o spegni
    // QUESTA annata». Il corpo porta perciò due cose — l'annata guardata e il
    // verso — e il verso si calcola sulla stessa annata, non sul campo
    // senz'anno. Stessa guardia sull'annata ignota di `togglePF`.
    const toggleAlert = async (user) => {
      if (!puoAmministrare) return;
      if (stagioneAttiva === null) return;

      try {
        await api.patch(`/users/${user.id}/alert`, {
          season: stagioneAttiva,
          alertFlag: !haAlertNellAnnata(user, stagioneAttiva)
        });

        await fetchUsers();
      } catch (err) {
        console.error("Error updating alert flag:", err);
        // Come sopra: il pallino resta com'era e nessuno direbbe perché.
        setMessaggio('Alert not updated. Please try again.');
      }
    };

  // 🔹 Il ritorno della finestra Payments: la finestra ha GIÀ scritto, qui si
  // rimette in pari solo l'elenco.
  //
  // ⛔️ PAY-SEASON F2 — È CADUTA LA DOPPIA SCRITTURA. Qui viveva un secondo
  // `PUT /fees/:id`, sparato dopo OGNI azione della finestra: la finestra
  // salvava, e questa funzione risalvava per conto suo leggendo il campo
  // piatto del socio appena ricaricato. Col contratto per-annata quel PUT
  // fallirebbe comunque (nessun `season` nel corpo, 400), ma il difetto era
  // già lì prima: una scrittura che nessuno ha chiesto, su una sede che il
  // chiamante non sceglie. Chi scrive le quote è la finestra, e una sola
  // volta.
  //
  // ⚠️ SI RILEGGE L'ELENCO, NON SI INFILA IL SOCIO RESTITUITO. La finestra
  // consegna quello che le ha risposto `GET /users/:id`, cioè il DOCUMENTO
  // GREZZO; l'elenco vive invece sulla PROIEZIONE di `GET /users`, che porta
  // campi che il documento non ha (`hasIdDocument`) e altri già normalizzati
  // (`status` con ripiego ACTIVE, `season`, `seasons`, `alertSeasons`,
  // `playFootballSeasons`). Sostituire una voce proiettata col documento
  // grezzo farebbe impallidire la riga di chi si è appena pagato la quota, in
  // punti che con le quote non c'entrano nulla. Si rilegge quindi l'elenco,
  // che è ciò che questa pagina fa già dopo il pallino Alert e le spunte Play
  // Football — e il socio restituito serve solo a dire «è cambiato qualcosa».
  //
  // `fetchUsers` porta con sé la guardia sul tipo (P9) e il ripiego a `[]`:
  // se `GET /users` tornasse qualcosa che array non è, la pagina non cade in
  // una schermata bianca subito dopo un salvataggio RIUSCITO. Porta anche il
  // banner del guasto, che qui prima non c'era: l'`alert()` di questa
  // funzione, che la nota di P9 lasciava fuori apposta, non ha più oggetto —
  // la scrittura non avviene più qui.
  const handleUpdateFees = async () => {
    await fetchUsers();
  };

  /*
   * ⛔️ SPOSTATA in UL-TESSERE — `teamList`, le voci della tendina Team.
   *
   * Nasceva qui, da `users` INTEGRALE, e offriva quindi le squadre di TUTTE
   * le annate presenti nei dati (e anche degli archiviati). Con la lettura
   * per annata sarebbe diventata una tendina che propone squadre che la
   * tabella non può mostrare: si sceglie un'annata, si filtra per una squadra
   * di un'altra, e l'elenco esce vuoto senza che nulla lo spieghi.
   *
   * Vive ora dopo `activeUsers`, e da `activeUsers` nasce: le sue voci sono
   * le squadre che si trovano davvero nella tabella che si sta guardando.
   * Il posto è obbligato, non una preferenza: qui `stagioneAttiva` non è
   * ancora dichiarata.
   */

  /*
   * ⛔️ RIMOSSO in F5 — `squadreEsistenti`, l'insieme dei NOMI delle squadre
   * del club (P7), e con esso lo stato `teams` e la lettura `fetchTeams`.
   *
   * Esisteva per UN solo scopo dichiarato: contare, nella colonna Team, le
   * sole squadre approvate che a una squadra vera corrispondessero. Da F5
   * quella colonna non conta più niente — scrive i NOMI che ogni tessera
   * porta — quindi l'insieme non ha più lettori. Lasciarlo avrebbe tenuto in
   * piedi una chiamata di rete a ogni apertura della pagina, al servizio di
   * nessuno.
   *
   * ⚠️ La tendina di filtro NON dipendeva da qui, e non dipende da qui
   * nemmeno ora: `teamList` nasce dalle TESSERE delle persone in elenco
   * (completamento F5), non dall'elenco delle squadre del club.
   */

  /* ===============================
     LE ANNATE SCEGLIIBILI, E QUALE È ATTIVA (S4)
  =============================== */
  // La tendina della stagione nasce da DUE sorgenti, unite:
  //
  //   1. le annate presenti nei DATI — `season` e `seasons[]` di ogni riga
  //      ricevuta, che è lo stesso metro con cui `teamList` (qui sopra) nasce
  //      dalle tessere delle persone in elenco e non da un elenco del club;
  //   2. i DUE anni del club — quello OPERATIVO e quello d'ISCRIZIONE — dalla
  //      sede condivisa, quando ci sono.
  //
  // ⚠️ Nel passo B il punto 2 è diventato DUE anni e non più uno solo, ed è
  // il perno dell'intero cambio: la pagina si apre sull'anno OPERATIVO, ma in
  // transizione l'anno d'iscrizione è un altro (operativo + 1) ed è proprio
  // la coorte che l'amministratore deve poter andare a guardare. Offrire un
  // anno solo gli toglierebbe metà del lavoro.
  //
  // ⚠️ Il punto 2 non è un di più. Al rollover il club apre le iscrizioni per
  // l'annata nuova e per un po' NESSUNO vi appartiene: senza questa unione
  // quell'annata non comparirebbe in tendina, e l'amministratore non potrebbe
  // guardare la coorte che sta costruendo — vedrebbe solo quella vecchia,
  // senza alcun segnale che ne esiste un'altra. Un'annata vuota che si può
  // scegliere dice la verità («nessuno ha ancora rinnovato»); un'annata che
  // non compare non dice niente.
  //
  // Ordine: dalla più recente alla più antica. È l'ordine in cui si guardano
  // le stagioni, e mette in cima quella su cui si lavora.
  const annateDeiDati = Array.from(
    users.reduce((elenco, u) => {
      const annate = Array.isArray(u?.seasons) ? u.seasons : [];
      annate.forEach((voce) => {
        const anno = annoDiLista(voce);
        if (anno !== null) elenco.add(anno);
      });
      const anno = annoDiLista(u?.season);
      if (anno !== null) elenco.add(anno);
      return elenco;
    }, new Set())
  ).sort((a, b) => b - a);

  // L'unione dei due insiemi. `Set` toglie da sé i doppioni — in stagione
  // unica i due anni del club sono lo stesso numero, e in tendina deve
  // comparire una volta sola.
  const stagioniDisponibili = Array.from(
    [operatingSeason, enrollmentSeason].reduce(
      (elenco, anno) => (anno == null ? elenco : elenco.add(anno)),
      new Set(annateDeiDati)
    )
  ).sort((a, b) => b - a);

  // Il DEFAULT — si calcola, non si assegna.
  //
  // ⚠️ Nessun `useEffect` che scriva `stagioneScelta` alla prima occasione, e
  // la ragione è pratica: un default assegnato per effetto va poi difeso da
  // se stesso (un interruttore «l'ho già fatto», una sentinella che distingua
  // la scelta dell'amministratore dal «non ancora scelto») e ogni difesa è un
  // punto in cui la sua scelta può essere sovrascritta alle sue spalle.
  // Calcolarlo lo rende impossibile per costruzione: finché non ha scelto
  // vale il default, dal momento in cui sceglie vale la sua scelta, e le due
  // cose non si toccano mai.
  //
  // ⚠️ PASSO B: il primo ramo è cambiato, e la scaletta a tre no. Il default
  // era l'annata d'ISCRIZIONE, ed era coerente con una pagina intesa come
  // sportello delle iscrizioni; è ora l'annata OPERATIVA, perché la User List
  // è l'elenco dei soci del club così com'è OGGI — chi gioca adesso. Chi deve
  // lavorare sulle nuove iscrizioni si sposta con la tendina, che quell'anno
  // continua a offrirlo (vedi `stagioniDisponibili`).
  //
  // I tre casi, in ordine — gli stessi di prima, letti sulla sede condivisa
  // invece che sui due stati locali che non esistono più:
  //   - stagione letta            → l'annata OPERATIVA del club;
  //   - lettura fallita ("error") → l'annata più recente presente nei dati
  //                                 (ripiego silenzioso, S4-PROPOSTA §2);
  //   - lettura in volo ("loading") → `null`: nessun default. Nei millisecondi
  //                                 dell'apertura la lista mostra tutto,
  //                                 invece di sfarfallare su un'annata
  //                                 sbagliata per poi cambiarla. (Da YS-5bis
  //                                 questo caso, a sessione aperta, non si
  //                                 presenta quasi mai: l'ingresso aspetta la
  //                                 stagione prima di disegnare l'app.)
  const stagionePredefinita =
    operatingSeason != null
      ? operatingSeason
      : seasonStatus === 'error'
        ? (annateDeiDati.length > 0 ? annateDeiDati[0] : null)
        : null;

  // L'annata che la pagina sta effettivamente guardando: la scelta se c'è,
  // altrimenti il default. `undefined` = non ha ancora scelto (vedi il blocco
  // dello stato di stagione).
  //
  // ⚠️ Vale `null` in un caso solo — default non calcolabile — e allora la
  // regola dell'anno non si applica alle tessere (`tesseraDellAnno` le lascia
  // passare tutte, di ogni annata): la coorte le legge con `stati: null`,
  // quindi entrano le tessere di ogni anno IN QUALUNQUE STATO, e lo stato
  // della persona (`statoDellAnno`, più sotto) è la regola di
  // `statoDelSoggetto` su tutte insieme — la stessa lettura che Team
  // Assignment fa in quel caso. Nessuno esce per via dell'anno: su un dato
  // che non si sa leggere non si fa sparire nessuno. Non è più «All seasons»:
  // quella era una scelta, questa è un'ignoranza dichiarata.
  const stagioneAttiva =
    stagioneScelta !== undefined ? stagioneScelta : stagionePredefinita;

  // STO GUARDANDO L'ANNO D'ISCRIZIONE? — il permesso della riga ambra «da
  // completare» (`ul-incomplete`, più sotto). Gemello di quello di Team
  // Assignment, e per la stessa ragione: `mustCompleteSeason` parla dell'anno
  // di ENROLLMENT e di quello solo, quindi si mostra dov'è vero e si spegne
  // sulle altre annate. Il rinnovante non è più marcato sull'anno che ha
  // completato.
  //
  // ⚠️ `Number.isInteger` prima dell'uguaglianza: con annata ignota
  // (`stagioneAttiva === null`) ed `enrollmentSeason` non letto (`null`) un
  // `===` crudo direbbe «sì» per caso.
  //
  // ⚠️ `annoDiLista` è il metro che questa pagina usa già per gli anni; è
  // idempotente sull'intero che `stagioneAttiva` è oggi, e regge se un domani
  // la tendina lasciasse passare la stringa del DOM.
  //
  // ⚠️ E LO STESSO METRO VA SUI DUE LATI, non solo a sinistra: oggi
  // `enrollmentSeason` arriva già intero o `null` dalla sede condivisa
  // (AuthContext), quindi `annoDiLista` lì è idempotente e il booleano non
  // cambia di un bit. Ma se un domani quel lato arrivasse come stringa, un
  // confronto asimmetrico direbbe «no» sempre e spegnerebbe l'evidenza IN
  // SILENZIO, ovunque. Simmetrico, il guasto non può nascondersi.
  const annoGuardato = annoDiLista(stagioneAttiva);
  const annoIscrizione = annoDiLista(enrollmentSeason);
  const guardoAnnoIscrizione =
    Number.isInteger(annoGuardato) && annoGuardato === annoIscrizione;

  /* ===============================
     LA COORTE D'ANNATA (S4) — a monte delle due basi
  =============================== */
  // ⚠️ QUI È IL PUNTO DELLA FETTA, e sta a monte di proposito.
  //
  // Il difetto da evitare era scritto: un selettore di stagione applicato al
  // solo `filteredUsers` (più sotto) darebbe una pagina che MOSTRA i soci di
  // un'annata e ne CONTA di tutte, senza che nulla segnali la discrepanza —
  // perché `activeUsers` e `statsUsers` nascevano tutt'e due da `users`
  // integrale. Applicando la stagione una volta sola, prima delle due basi,
  // la tabella e le card parlano per costruzione della stessa annata.
  //
  // ⚠️ «Coorte» vuol dire filtrata per STAGIONE, non dai quattro filtri.
  // Le card NON seguono ricerca, ruolo, squadra e saldo: se seguissero
  // `filteredUsers`, «Total Members» cambierebbe mentre si digita un nome.
  // E la decisione 4 dell'8/8 resta intera — la tabella mostra ACTIVE +
  // PENDING, le card contano i soli ACTIVE: le due basi restano DUE, con lo
  // stesso metro di prima, ma sulla stessa annata.
  //
  // ⚠️ UL-TESSERE CAMBIA LA SORGENTE DELL'APPARTENENZA, e non è una riscrittura
  // della stessa cosa. Prima: «questa PERSONA è dell'annata X?», letta sul
  // documento (`season`/`seasons[]`). Ora: «questa persona ha almeno una
  // TESSERA dell'annata X?» — cioè una tessera il cui `cardSeason` vale
  // quell'anno, IN QUALUNQUE STATO (`stati: null`, v. il paragrafo «per anno
  // guardato» più sotto; fino a quel cantiere qui contavano le sole ACTIVE).
  // Chi entrava per il proprio `season` ma non ha tessere dell'annata NON
  // compare più (era la riga muta del caso U3: compariva con Role e Team
  // vuoti, e non diceva nulla di vero sull'annata).
  //
  // ⚠️ PIÙ L'ECCEZIONE ADMIN (opzione X), che è un OPPURE e non un filtro in
  // più: un ADMIN o SUPERADMIN entra nella COORTE in ogni annata, anche senza
  // tessere dell'anno. Ma la coorte non è la tabella: la visibilità la
  // decidono le due basi sotto, con `statoDellAnno`, per lui come per
  // chiunque. Un amministratore SENZA tessere dell'anno guardato compare per
  // RIPIEGO (lo stato è quello della persona, `statoDi`); uno che HA tessere
  // dell'anno segue l'anno guardato come tutti — e se quelle tessere sono
  // TUTTE ARCHIVED, su quell'annata è ARCHIVED e NON compare in tabella (né
  // nelle card). Le sue tessere restano soggette alla regola come per
  // chiunque — l'eccezione fa entrare la PERSONA e il suo cappello, non le
  // sue squadre di altre annate (vedi `includiAmministrative`, in testa al
  // file).
  // Senza questa clausola gli amministratori sparirebbero dalla pagina che
  // amministrano: le loro tessere amministrative non portano `cardSeason` e
  // non possono portarlo.
  //
  // ⚠️ «USER LIST PER ANNO GUARDATO» (YS-13 Punto 3): LA COORTE È PER ANNO,
  // NON PIÙ PER ANNO-E-ACTIVE. `stati: null` è la lettura di Team Assignment
  // (D1): entra chi ha almeno una tessera dell'anno IN QUALUNQUE STATO —
  // anche il nuovo iscritto con la sola tessera PENDING, che prima non
  // compariva affatto e che l'amministratore deve vedere. La VISIBILITÀ non
  // si decide qui: la decidono le due basi sotto, con lo stato dell'anno
  // (`statoDellAnno`) — la tabella toglie chi è ARCHIVED, le card contano i
  // soli ACTIVE. Prima il filtro «solo ACTIVE» stava qui a monte, e la
  // decisione 4 dell'8/8 (tabella ACTIVE+PENDING) era di fatto lettera morta
  // per chi aveva tessere PENDING soltanto.
  const coorteDiStagione = users.filter(
    (u) =>
      tessereDelSoggetto(u?.contexts, stagioneAttiva, { stati: null }).length >
        0 || eAmministratore(u)
  );

  // LO STATO DELLA PERSONA PER L'ANNO GUARDATO — il metro unico delle tre
  // letture di stato di questa pagina (base della tabella, base delle card,
  // riga ambra). È `statoDelSoggetto` (utils/squadre.js): le tessere di
  // `stagioneAttiva` in qualunque stato, con la precedenza di Team Assignment
  // — PENDING vince, poi tutte ARCHIVED, altrimenti ACTIVE («vince lo stato
  // più indietro»). Così, guardando l'anno operativo, questa pagina e Team
  // Assignment dicono lo stesso stato della stessa persona.
  //
  // ⚠️ NON È LA PRECEDENZA DELLO SPECCHIO BACKEND (ACTIVE > PENDING >
  // ARCHIVED, che risponde a «può entrare?»): qui non si scrive nulla e non
  // si ricalcola nessuno `status`.
  //
  // ⚠️ IL RIPIEGO È `statoDi(u)`, e vale per chi NON ha tessere dell'anno —
  // gli amministratori (tessere senza anno, in lista per eccezione): per loro
  // lo `status` della persona resta l'unica lettura, con il metro tollerante
  // di `statoDi` (assente = ACTIVE, ignoto = "" → in tabella senza evidenza,
  // fuori dalle card). Per chiunque abbia tessere dell'anno `u.status` non si
  // legge più.
  const statoDellAnno = (u) =>
    statoDelSoggetto(u?.contexts, stagioneAttiva, {
      ripiegoPersona: statoDi(u)
    });

  // ===============================
  // LE DUE BASI — tabella e card contano popolazioni DIVERSE
  // ===============================
  //
  // Prima di P2 ce n'era una sola (`activeUsers`) e serviva a entrambe:
  // le card contavano quindi anche gli iscritti in attesa. La decisione 4
  // dell'8/8/2026 le separa, e la separazione va tenuta: chi tocca l'una
  // non deve credere di lasciare l'altra ferma.

  // Base della TABELLA — P8: criterio PER ESCLUSIONE TOLLERANTE.
  // Gli iscritti PENDING restano in elenco: sono pratiche da lavorare, e
  // l'admin deve vederle (decisione 4 dell'8/8: la tabella mostra ACTIVE +
  // PENDING, le card contano i soli ACTIVE). L'evidenza in ambra della riga
  // in attesa è più sotto, nel corpo della tabella.
  //
  // ERA: `users.filter(u => u.status !== "ARCHIVED")` — confronto CRUDO. Un
  // documento archiviato in grafia storica (`status: "Archived"`) non veniva
  // escluso e restava in elenco: dopo P3 la sua riga era anche pienamente
  // operabile (bottone Payments e spunte PF, riconosciuti in qualunque
  // grafia). Ora `statoDi` lo riconosce e la riga esce.
  //
  // ⚠️ POLITICA SCELTA DAL CAPOMASTRO, e non è una riscrittura della stessa
  // cosa: si NASCONDE ciò che è archiviato, non si AMMETTE ciò che è attivo o
  // in attesa. Le due vie divergono su un caso solo — lo stato IGNOTO (un
  // refuso, un "SUSPENDED", un valore futuro): per ammissione la persona
  // SPARIREBBE dall'unica pagina che la governa, e sparirebbe in silenzio;
  // per esclusione resta visibile, senza evidenza, perché non si finge di
  // sapere che stato abbia. È la dottrina già in opera qui (guardia del
  // contatore TD, P7): su un dato che non si sa leggere non si inventa
  // nulla, e far sparire una riga è la forma più forte di invenzione.
  //
  // Il nome `activeUsers` resta quello di prima — è la base della tabella e
  // contiene anche i PENDING: fuorviante già da prima di P8, rinominarlo
  // tocca punti fuori perimetro. Registrato come residuo.
  // ⚠️ S4: la base è `coorteDiStagione`, non più `users`. Il criterio dello
  // stato non cambia di una virgola — cambia soltanto l'insieme di partenza,
  // che ora è l'annata scelta invece di tutte.
  // ⚠️ «USER LIST PER ANNO GUARDATO»: il metro è ora lo STATO DELL'ANNO
  // GUARDATO (`statoDellAnno`), non più lo `status` della persona. La
  // politica per esclusione resta la stessa: esce chi, per le tessere di
  // quest'anno, è ARCHIVED (o, senza tessere dell'anno, lo è la persona).
  // Un archiviato del 2026 con tessera 2027 ACTIVE compare sul 2027 e non
  // sul 2026; un nuovo iscritto con la sola tessera 2027 PENDING compare sul
  // 2027, in ambra.
  const activeUsers = coorteDiStagione.filter(
    (u) => statoDellAnno(u) !== "ARCHIVED"
  );

  // LE VOCI DELLA TENDINA DI FILTRO — i nomi di squadra che compaiono nelle
  // TESSERE VALIDE delle persone IN TABELLA.
  //
  // ⚠️ Non nasce più da `user.team` (una squadra sola per persona, e ferma da
  // F3b): una squadra che vive solo nelle tessere — il caso normale dopo
  // F3a/F3b — non aveva alcuna voce, e filtrarla era impossibile.
  //
  // ⚠️ UL-TESSERE — DUE CAMBIAMENTI, ed è lo stesso: la sorgente è
  // `activeUsers` (la base della TABELLA) invece di `users` integrale, e le
  // squadre sono quelle delle tessere VALIDE dell'annata guardata. La tendina
  // offre così esattamente le squadre che il filtro può trovare: prima
  // proponeva le squadre di ogni annata e di ogni archiviato, e sceglierne una
  // fuori annata dava un elenco vuoto senza spiegazione.
  //
  // ⚠️ NIENTE VALORI-SPIA. L'elenco contiene solo NOMI VERI perché tali sono
  // i `teams` che la proiezione manda (vedi `squadreDellaPersona`): «ALL» è
  // già diventato `allTeams:true` e non è un nome, «TEAMTD» non è mai stato
  // una squadra. E «tutte le squadre» NON è una voce di filtro: non è una
  // squadra del club, è un permesso — chi ce l'ha corrisponde a ogni voce
  // vera (`haLaSquadra`), che è il modo giusto di dirlo in una tendina di
  // squadre.
  //
  // Doppioni tolti a confronto normalizzato, e a schermo va la prima grafia
  // incontrata: «U12» e «u12» sono una voce sola, non due.
  const teamList = Array.from(
    activeUsers.reduce((elenco, u) => {
      squadreDellaPersona(u, stagioneAttiva).nomi.forEach((nome) => {
        const chiave = normalizza(nome);
        if (!elenco.has(chiave)) elenco.set(chiave, nome);
      });
      return elenco;
    }, new Map()).values()
  ).sort((a, b) => sortStrings(normalizza(a), normalizza(b)));

  // ===============================
  // CARD (otto per il SUPERADMIN, cinque per l'ADMIN: le tre del denaro
  // sono del solo SUPERADMIN, cantiere ADMIN-SA del 26/9/2026)
  // — decisione 4 dell'8/8/2026: contano SOLO gli ACTIVE
  // ===============================

  // Membro attivo: status ACTIVE, oppure status ASSENTE.
  // «Assente vale ACTIVE» è il metro del backend (server.js), che i
  // documenti storici senza il campo li tratta da attivi; da P8 quella
  // regola vive in `statoDi`, che l'assenza la restituisce già come ACTIVE.
  //
  // ⚠️ P8 non cambia CHI conta: il criterio è lo stesso di prima, riscritto
  // sulla sede unica invece che su una seconda lettura del campo. Uno stato
  // IGNOTO resta fuori dalle card (`statoDi` lo rende ""), esattamente come
  // prima; PENDING e ARCHIVED restano fuori come da decisione 4. Il punto
  // rinviato a P8 dal commento di P2 — «promuovere l'helper?» — è sciolto
  // così: la sede unica c'è, ma resta dentro questa pagina (vedi `statoDi`).
  // ⚠️ «USER LIST PER ANNO GUARDATO»: conta chi, per le tessere dell'anno
  // guardato, è ACTIVE (`statoDellAnno`); `statoDi` resta il solo ripiego di
  // chi tessere dell'anno non ne ha (gli amministratori). PENDING e ARCHIVED
  // dell'anno restano fuori come da decisione 4 — e con loro il caso limite,
  // voluto e coerente con Team Assignment, della persona che nello stesso
  // anno ha una tessera ACTIVE e una PENDING: è PENDING, e non si conta.
  const isActiveMember = (u) => statoDellAnno(u) === "ACTIVE";

  // ⚠️ S4: stessa sostituzione di `activeUsers`, e per la stessa ragione —
  // le card devono contare l'annata che la tabella mostra. Da qui discendono
  // tutti i riquadri in cima alla pagina (otto, o cinque per l'ADMIN), che
  // quindi si allineano senza altri interventi.
  const statsUsers = coorteDiStagione.filter(isActiveMember);

  const totalMembers = statsUsers.length;

  /* ===============================
     GIOCATORI — per ruolo o per context attivo
  =============================== */
  // Sede unica (hasRole → canAccess): una sola riga al posto delle due
  // versioni divergenti di prima (UsersList guardava anche i contexts,
  // Dashboard solo il ruolo). Col vocabolario di P0 entrano anche i
  // documenti storici `role: "Child"`, che il vecchio confronto
  // `toLowerCase() === 'player'` non vedeva, e i context salvati con
  // `status: "Active"`, che il filtro crudo scartava in silenzio.
  // ⚠️ UL-TESSERE: il gate unico resta `hasRole`, ma la domanda si pone sulla
  // persona PROIETTATA sull'annata guardata — le sue tessere valide, più il
  // cappello admin. Senza, la card conterebbe come giocatore chi ha una
  // tessera PLAYER di un'altra annata: un numero che la tabella non mostra.
  const playerUsers = statsUsers.filter(
    u => hasRole(personaDellAnnata(u, stagioneAttiva), "PLAYER")
  );

  const players = playerUsers.length;

  const percPlayers = totalMembers
    ? Math.round((players / totalMembers) * 100)
    : 0;

  /* ===============================
     GENERE — fra i soli GIOCATORI (cantiere UL-SCHEDE-GENERE, 15/9/2026)
  =============================== */
  // ⚠️ CAMBIO DI DEFINIZIONE, voluto dal committente. Dal 9/9 (UL-GENERE) le
  // due schede contavano il genere su TUTTI i membri della coorte, ma il
  // numero non si leggeva: Male + Female non tornava a Total Members (il
  // genere dei genitori è per lo più non registrato) e il confronto con
  // Players non aveva senso aritmetico. Le quattro schede in cima diventano
  // una famiglia: un totale, poi i soli giocatori scomposti per genere —
  // «Players Male» e «Players Female» contano fra i GIOCATORI dell'annata,
  // cioè dentro lo stesso `playerUsers` che alimenta «Only Players».
  //
  // ⚠️ SI CONTA IL GENERE, NON LA CATEGORIA ANAGRAFICA. `categoriaAnagrafica`
  // qui non serve e sarebbe sbagliata: pretende una data di nascita
  // leggibile, e un giocatore maschio senza `dob` non è né MEN né BOYS — ma
  // è un maschio, e nella scheda Players Male ci deve stare. Il criterio è il
  // solo genere.
  //
  // La lettura del genere è la SEDE UNICA già in opera, `genereCanonico`
  // (utils/categoria.js), la stessa che alimenta le pastiglie di riga: sa
  // entrambe le grafie in circolazione — "M"/"F" del modello nuovo e
  // «Male»/«Female» dell'importazione storica. Il vecchio `normalizeGender`
  // locale conosceva SOLO la grafia lunga, e per questo la scheda Female
  // mostrava 0 pur essendoci socie femmine: le loro schede hanno "F".
  // Genere vuoto o «Other» non entra in nessuna delle due card.
  //
  // ⚠️ NÉ IL PREDICATO DI RUOLO NÉ LA LETTURA DEL GENERE SI RISCRIVONO QUI:
  // l'insieme dei giocatori è `playerUsers` (hasRole su personaDellAnnata,
  // sopra), e si filtra quello. `statsUsers` resta la coorte di Total
  // Members e delle altre schede: cambia l'insieme su cui si classifica il
  // genere (i giocatori, sottoinsieme della coorte), non la coorte.
  const males = playerUsers.filter(
    u => genereCanonico(u.gender) === 'M'
  ).length;

  const females = playerUsers.filter(
    u => genereCanonico(u.gender) === 'F'
  ).length;

  // ⚠️ IL DENOMINATORE SEGUE IL NUMERATORE: i numeratori attraversano i soli
  // giocatori, quindi la percentuale è su `players` — si legge «sui giocatori
  // di questa annata», e Male% + Female% torna a 100 quando ogni giocatore
  // ha il genere registrato (a meno dell'arrotondamento). Con zero giocatori
  // la percentuale è 0, senza divisione per zero.
  const percM = players
    ? Math.round((males / players) * 100)
    : 0;

  const percF = players
    ? Math.round((females / players) * 100)
    : 0;

  /* ===============================
     NUOVI / RINNOVATI — fra i soli GIOCATORI della card «Only Players»
  =============================== */
  // Rinnovato = chi, dentro `playerUsers`, ha almeno una tessera dell'anno
  // PRECEDENTE a quello guardato, in QUALUNQUE stato (anche ARCHIVED: l'anno
  // scorso c'era). La regola dell'anno è quella di `tessereDelSoggetto`, non
  // se ne scrive un'altra. Nuovo = il resto, PER DIFFERENZA: così
  // New + Renewed torna sempre a `players`.
  //
  // ⚠️ La riga si rende solo con anno guardato LEGGIBILE e diverso dal 2026:
  // il 2026 è il primo anno del modello a tessere, e un «anno prima» da
  // confrontare non c'è. Con annata ignota `tessereDelSoggetto` lascerebbe
  // passare le tessere di ogni anno: per questo la guardia viene PRIMA.
  const mostraNuoviRinnovati =
    Number.isInteger(annoGuardato) && annoGuardato !== 2026;

  const renewedPlayers = mostraNuoviRinnovati
    ? playerUsers.filter(
        u =>
          tessereDelSoggetto(u?.contexts, annoGuardato - 1, { stati: null })
            .length > 0
      ).length
    : 0;

  const newPlayers = players - renewedPlayers;

  const percNew = players ? Math.round((newPlayers / players) * 100) : null;

  /* ===============================
     QUOTE — una passata sola, con guardia sul tipo
  =============================== */
  // GUARDIA SUL TIPO di `totalDue` (decisione 4 per la media; estesa a
  // Outstanding Fees per decisione del capomastro dell'8/8, D3).
  // Senza guardia:
  //   - `totalFeesDue += "150"` CONCATENA invece di sommare, e la media
  //     diventa un valore inventato;
  //   - `"abc" - pagato` dà NaN, e basta un documento malformato perché
  //     l'intera card Outstanding mostri "$NaN".
  //
  // ⚠️ SCARTARE VUOL DIRE ESCLUDERE IL DOCUMENTO, NON ISCRIVERLO A ZERO.
  // La differenza non è formale: un tesserato con quota scritta male e
  // pagamenti registrati, messo a zero, farebbe CALARE l'insoluto del club
  // di quanto ha versato (`0 - pagato`). Sarebbe un numero peggiore di
  // quello di prima della fetta, e per giunta plausibile a vederlo.
  // Quindi: quota malformata → il documento esce dai due totali del
  // DOVUTO, mentre l'INCASSO si conta comunque, perché quei soldi sono
  // stati versati davvero.
  //
  // ⚠️ QUOTA ASSENTE ≠ QUOTA MALFORMATA. `totalDue` mancante significa
  // «nessuna quota impostata», vale 0 ed è un dato legittimo: si comporta
  // come prima di P2. Si scarta solo ciò che è scritto male: una stringa,
  // un oggetto, un booleano — e anche NaN o Infinity, che per JavaScript
  // sono `typeof "number"` e passerebbero indenni una guardia sul solo
  // tipo. Da qui `Number.isFinite`, che li respinge tutti insieme.
  // (Oggi NaN e Infinity non arrivano dai dati: il backend risponde in
  // JSON, che li converte in null. Ma la guardia deve fare quello che
  // dice di fare, non quello che i dati di oggi le consentono.)
  //
  // ⚠️ IL PREZZO, DETTO PER INTERO. La guardia risana Average Fee, dove
  // una quota-stringa concatenava e produceva una media inventata. Su
  // Outstanding invece può TOGLIERE il contributo di un documento che la
  // coercizione di JavaScript indovinava per caso: `"150" - 50` faceva
  // 100, ed era giusto; ora quel documento vale 0 e l'insoluto del club
  // scende di 100. Si accetta perché la stessa coercizione, su un valore
  // non convertibile, distrugge l'intera card con "$NaN": si preferisce
  // perdere un importo scritto male che falsare le due cifre che
  // dipendono da `totalDue` — Outstanding Fees e Average Fee.
  // (Fees Collected non è in gioco: si somma dai soli `payments`, prima
  // della guardia, e da `totalDue` non dipende.)
  //
  // ⚠️ COSA QUESTA GUARDIA NON COPRE: `Number(p.amount)` qui sotto resta
  // senza difesa, quindi un singolo pagamento con importo non numerico può
  // ancora produrre "$NaN" su Fees Collected e Outstanding. Difetto
  // preesistente, fuori da D3: non lo si chiude di iniziativa.
  // Il rimedio a monte (validare i tipi in scrittura) è del cantiere che
  // tocca il salvataggio delle quote.
  //
  // ⚠️ PAY-SEASON F2: LE TRE CIFRE SONO DELL'ANNATA GUARDATA. Leggevano il
  // campo piatto `fees` per chiunque, cioè dicevano sempre la stagione 2026
  // qualunque anno la pagina stesse guardando — mentre la coorte sotto di
  // loro (`statsUsers`) l'annata la rispettava già. Ora la sede la sceglie
  // `quoteDellAnnata`: sul 2026 è il campo piatto e le cifre non cambiano di
  // un centesimo; su un'altra annata sono le quote di quell'annata, e chi non
  // ne ha vale zero — non eredita il dovuto del 2026.
  //
  // ⚠️ IL RECORD ORA C'È SEMPRE (è la forma che il lettore garantisce), e il
  // vecchio `if (u.fees)` non ha più oggetto: chi non ha quote dell'annata
  // porta `payments` vuoto e `totalDue` assente, cioè contribuisce zero a
  // tutt'e tre. La guardia sulla quota malformata resta identica, e resta sul
  // valore GREZZO — il lettore non converte i tipi apposta per non renderla
  // cieca.
  let totalCollected = 0;
  let totalOutstanding = 0;
  let totalFeesDue = 0;

  statsUsers.forEach(u => {
    const quote = quoteDellAnnata(u, stagioneAttiva);
    const paid = totaleVersato(quote);

    // L'incasso si conta sempre: è denaro entrato, qualunque cosa dica
    // la quota.
    totalCollected += paid;

    const rawDue = quote.totalDue;

    // Quota malformata: fuori dai totali del dovuto, senza sottrazioni.
    if (rawDue != null && !Number.isFinite(rawDue)) return;

    const due = rawDue || 0;

    totalOutstanding += due - paid;
    totalFeesDue += due;
  });

  const averageFeePerPlayer =
    players > 0 ? totalFeesDue / players : 0;

  /* ===============================
     PLAY FOOTBALL — definizione unica (decisione 3)
  =============================== */
  // L'elenco letterale di ruoli che stava qui è sparito in P2: la domanda
  // «chi è idoneo?» ha una sola sede, utils/playFootball.js.
  //
  // ⚠️ DA P3 LA USANO ANCHE LE SPUNTE DI RIGA, e con ciò si chiude lo stato
  // intermedio dichiarato in P2 (card piena e righe a «—»): il criterio è lo
  // stesso qui e nella cella PF della tabella.
  // Resta invece diversa, di proposito, la POPOLAZIONE: la card conta
  // `statsUsers` (solo ACTIVE, decisione 4), la tabella mostra `activeUsers`
  // (ACTIVE + PENDING). Un giocatore PENDING ha quindi le spunte in riga ma
  // non entra in questo denominatore. È voluto — non è la discordanza di P2.
  // ⚠️ UL-TESSERE: stessa proiezione della card Players, e per la stessa
  // ragione. La sede unica dell'idoneità (utils/playFootball.js) non cambia
  // e non riceve un anno: riceve una persona già proiettata sull'annata.
  const pfEligibleUsers = statsUsers.filter(
    u => isPlayFootballEligible(personaDellAnnata(u, stagioneAttiva))
  );

  // ⚠️ PFA: il numeratore legge ora i due valori DELL'ANNATA GUARDATA, non più
  // il campo senz'anno. Con ciò la card diventa coerente per la prima volta:
  // il denominatore era già per annata (`personaDellAnnata` qui sopra), il
  // numeratore no, e cambiando «Season Viewed» si muoveva un solo termine
  // della frazione.
  const pfCompletedCount = pfEligibleUsers.filter(u => {
    const pf = playFootballDellAnnata(u, stagioneAttiva);
    return pf.registered === true && pf.feePaidByClub === true;
  }).length;

  const pfEligibleCount = pfEligibleUsers.length;

  const pfCompletionPerc =
    pfEligibleCount > 0
      ? Math.round((pfCompletedCount / pfEligibleCount) * 100)
      : 0;

  // Filtro utenti
  const filteredUsers = activeUsers.filter((user) => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const matchName = fullName.includes(search.toLowerCase());
    // Filtro dei ruoli: gate unico, non uguaglianza di stringhe
    // (decisione del capomastro del 9/8, D-C).
    //
    // ⚠️ PERCHÉ `hasRole` E NON `isActingAs`, che la decisione 1 dell'8/8
    // prescrive «per le viste personali e i FILTRI»: quella regola parla del
    // cappello indossato da CHI GUARDA («la mia settimana», «le mie
    // squadre»). Qui il filtro interroga il ruolo DELLE RIGHE, cioè di terzi:
    // il cappello di chi guarda non c'entra, e `isActingAs` — che risponde
    // sull'utente autenticato — non avrebbe senso.
    // Le voci della tendina valgono ora i ruoli CANONICI (PLAYER, PARENT,
    // COACH, ADMIN, SUPERADMIN), quindi una voce sola raccoglie tutte le
    // grafie e i ruoli storici che il vocabolario traduce: «Coach» prende
    // anche Manager, TD e ManagerTD, «Player» prende anche Child.
    // Con `hasRole` contano anche i context ATTIVI, come per la card Players
    // e per le colonne Role e Team di questa stessa tabella: un filtro cieco
    // ai context darebbe un elenco in contraddizione con ciò che si legge
    // nella riga.
    // ⚠️ UL-TESSERE: sulla persona PROIETTATA sull'annata — le tessere valide
    // più il cappello admin, cioè esattamente ciò che la colonna Role mostra.
    // Un coach del 2026 non si trova più filtrando «Coach» nel 2027; un
    // amministratore si trova sempre sotto «Admin», in ogni annata, perché la
    // sua riga in ogni annata dice «Admin».
    const matchRole = filterRole
      ? hasRole(personaDellAnnata(user, stagioneAttiva), filterRole)
      : true;
    // Filtro delle squadre: le TESSERE ATTIVE, non `user.team`.
    //
    // ⚠️ Era la stessa contraddizione del filtro dei ruoli prima di D-C, ma
    // sulle squadre: la riga mostrava le squadre delle tessere (F5) e il
    // filtro interrogava un campo che quella riga non scrive più. Un coach
    // dell'U12 con la squadra solo in tessera spariva filtrando per «U12»,
    // pur avendo «U12» scritto nella propria cella Team.
    // Il criterio sta in `haLaSquadra`, una volta sola, insieme a quello
    // della tendina e dell'ordinamento.
    // ⚠️ UL-TESSERE: le squadre delle sole tessere VALIDE dell'annata, come
    // la colonna Team e come la tendina qui sopra.
    const matchTeam = filterTeam
      ? haLaSquadra(user, filterTeam, stagioneAttiva)
      : true;
    // ⚠️ PAY-SEASON F2: il saldo del filtro è quello DELL'ANNATA guardata, lo
    // stesso che la riga mostra nella colonna Balance. Erano due letture della
    // stessa cosa scritte due volte — ora la sede è una — e leggevano
    // entrambe il campo piatto: filtrando «Outstanding» in un'annata senza
    // quote sarebbero comparsi i debitori del 2026.
    const quoteDelSocio = quoteDellAnnata(user, stagioneAttiva);
    const totalDue = quoteDelSocio.totalDue || 0;
    const paid = totaleVersato(quoteDelSocio);
    const balance = totalDue - paid;
    const matchBalance =
      filterBalance === 'positive' ? balance > 0 : filterBalance === 'zero' ? balance === 0 : true;
    return matchName && matchRole && matchTeam && matchBalance;
  });

  

  // Ordinamento
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let res = 0;
    // Member — CHIAVE COMPOSTA. La colonna mostra «Nome Cognome» in un'unica
    // cella, quindi ordina su un'unica chiave: il nome, e a parità il
    // cognome come spareggio. È la continuazione del default di prima
    // (`firstName`): non si perde l'ordine per cognome, gli si dà il secondo
    // posto invece di una colonna propria. Il comparatore è lo stesso
    // `sortStrings` grezzo di tutte le altre colonne — non `localeCompare`:
    // qui si fonde una colonna, non si cambia il metro del confronto.
    if (sortBy === 'member') {
      res = sortStrings(a.firstName, b.firstName);
      if (res === 0) res = sortStrings(a.lastName, b.lastName);
    }
    // Ordinamento per ruolo in forma CANONICA (decisione del capomastro del
    // 9/8, D-E): senza, «PLAYER» e «Player» finiscono in due blocchi lontani
    // dell'elenco (le maiuscole ordinano prima delle minuscole) e un
    // «Manager» non sta mai vicino ai «Coach». La colonna continua a mostrare
    // la grafia originale: qui si ordina, non si traduce.
    else if (sortBy === 'role') res = sortStrings(canonicalRole(a.role), canonicalRole(b.role));
    // Ordinamento per squadra sulle TESSERE ATTIVE (completamento F5): la
    // colonna mostra quelle, e una colonna che si ordina per un dato che non
    // mostra sposta le righe senza che si capisca perché. La chiave e il suo
    // verso stanno in `confrontaSquadre`.
    else if (sortBy === 'team') res = confrontaSquadre(a, b, stagioneAttiva);
    return sortDir === 'asc' ? res : -res;
  });

  // Per cambio ordinamento colonne
  const handleSort = (column) => {
    if (sortBy === column) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  /* ===============================
     L'ANNATA DELLA PASTIGLIA CATEGORIA (S5) — dov'è finita (passo B)
  =============================== */
  // ⛔️ Qui viveva `annataDellaPastiglia`, che era `stagioneAttiva` con un
  // ripiego: «se non c'è un'annata scelta (era il caso «All seasons») prendi
  // quella d'iscrizione del club». Tolta «All seasons», quel caso non si
  // presenta più: `stagioneAttiva` vale `null` soltanto quando NESSUN anno è
  // noto — e allora nemmeno l'anno del club lo è, perché è la stessa lettura
  // ad essere mancata. Il ripiego era diventato inerte per costruzione, e un
  // ripiego che non può scattare è peggio di nessun ripiego: si legge come
  // una difesa che non c'è.
  // La pastiglia usa ora `stagioneAttiva` di persona, al suo punto d'uso.

  return (
    <HomeShell backTo="/home" width="admin">
      {/* Telaio comune (doc 07 §7): barra utente, banda con «‹ Back» e
          «Switch role», colonna degli strumenti admin — a tutta finestra sopra
          i 768px (§8.1), come Team Assignment e Manage User Contexts. Il logo
          esce di qui: vive nel cerchio della barra utente, e il §9 non prevede
          altri usi a schermo. Il titolo è la classe condivisa (§3.1, centrato
          in Rosso QPSC) e porta il nome della voce di menù. */}
      <h1 className="qpsc-page-title">User List Dashboard</h1>

      {/* LA BARRA DI STATO DELLA STAGIONE (doc 07 §8.2) — nuda, senza
          `override` e senza `note`.
          Nuda perché questa pagina la stagione la LEGGE e non la comanda:
          l'`override` serve a chi scrive la cassettina e subito dopo la
          conosce meglio del contesto (Season Management), e la `note` parla
          di un gesto che qui non si fa. `showPhase` e `showEnrollments`
          restano ai loro default: la versione intera.
          Sta qui, sopra le schede, perché dichiara la cornice dentro cui i
          numeri delle schede vanno letti. Non è un filtro: il filtro è
          «Season Viewed», più sotto nella barra dei comandi.
          ⚠️ Lo spazio verticale non serve scriverlo: la barra è figlia
          diretta della colonna del telaio (`.qpsc-column`, HomeShell.css),
          che ha `gap: var(--qpsc-space-16)` fra i blocchi — gli stessi 16px
          che Season Management si scrive a mano perché là la barra sta
          dentro una `<section>`. Vedi UsersList.css §1-bis. */}
      <SeasonStatusBar />

      <div className="ul-cards">
        <div className="ul-card">
          <span className="ul-card__title">Total Members</span>
          <span className="ul-card__value">{totalMembers}</span>
        </div>

        <div className="ul-card">
          <span className="ul-card__title">Only Players</span>
          <span className="ul-card__value">
            {players}
            <span className="ul-card__percent">({percPlayers}%)</span>
          </span>
          {/* Nuovi / rinnovati: testo secondario, nessun colore (§8.1: nelle
              card il colore non classifica). Assente sul 2026 e ad annata
              ignota — non una riga vuota. */}
          {mostraNuoviRinnovati && (
            <span className="ul-card__note">
              New {newPlayers}
              {percNew !== null && ` (${percNew}%)`}
            </span>
          )}
        </div>

        {/* §1.5: il valore non si colora più di blu e di rosso — il genere è
            un dato, e i colori dell'app non classificano i dati. Le due
            schede contano fra i soli giocatori, con la % sui giocatori
            (UL-SCHEDE-GENERE, 15/9/2026). */}
        <div className="ul-card">
          <span className="ul-card__title">Players Male</span>
          <span className="ul-card__value">
            {males}
            <span className="ul-card__percent">({percM}%)</span>
          </span>
        </div>

        <div className="ul-card">
          <span className="ul-card__title">Players Female</span>
          <span className="ul-card__value">
            {females}
            <span className="ul-card__percent">({percF}%)</span>
          </span>
        </div>

        {/* §8.1 deroga (iii) al §1.5: il denaro si colora per segno, come in
            Club Finance — incassato in verde, dovuto in rosso leggibile.
            Cantiere ADMIN-SA (26/9/2026): le tre card del denaro sono del
            solo SUPERADMIN (`soloSuperAdmin`); per l'ADMIN non si montano e
            `.ul-cards`, flex-wrap, ridispone le altre cinque da se'. */}
        {soloSuperAdmin && (
          <>
            <div className="ul-card">
              <span className="ul-card__title">Fees Collected</span>
              <span className="ul-card__value ul-amount--paid">
                {denaroAschermo(totalCollected)}
              </span>
            </div>

            <div className="ul-card">
              <span className="ul-card__title">Outstanding Fees</span>
              <span className="ul-card__value ul-amount--due">
                {denaroAschermo(totalOutstanding)}
              </span>
            </div>

            <div className="ul-card">
              <span className="ul-card__title">Average Fee / Player</span>
              <span className="ul-card__value">
                {denaroAschermo(averageFeePerPlayer)}
              </span>
            </div>
          </>
        )}

        <div className="ul-card">
          <span className="ul-card__title">Play Football</span>
          <span className="ul-card__value">
            {pfCompletedCount}/{pfEligibleCount}
            <span className="ul-card__percent">
              ({pfCompletionPerc}%)
            </span>
          </span>
        </div>
      </div>

      <div className="ul-toolbar">
        {/* Ogni campo dentro il guscio condiviso, con l'etichetta sopra (doc
            07 §8, §8.1: barra filtri come Team Assignment). Le etichette sono
            testi nuovi, decisi in chat e scritti nella norma prima del codice;
            il segnaposto del campo di ricerca resta quello di sempre. */}
        <div className="qpsc-field">
          <span className="qpsc-field__label">Search</span>
          <input
            className="qpsc-input"
            type="text"
            aria-label="Search by name"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {/* IL SELETTORE DI STAGIONE (S4).
            Sta in PRIMA posizione fra le tendine perché non è un filtro fra i
            filtri: sceglie la COORTE su cui gli altri tre lavorano. Ruolo,
            squadra e saldo restringono un insieme; la stagione dice quale sia
            quell'insieme.

            ⚠️ S4 lo lasciò NUDO, senza classe e senza una riga di CSS, perché
            la veste della pagina era materia del cantiere grafico. È questa
            fetta (S5): il selettore prende ora il guscio e l'etichetta degli
            altri campi, e diventa omogeneo. Il COMPORTAMENTO non è toccato —
            stesso valore, stesso `onChange`, stesse voci.

            ⚠️ Il valore viaggia come STRINGA nel DOM e come NUMERO nello
            stato. La conversione sta qui e in un punto solo.

            ⚠️ PASSO B — due cambi, e sono di sostanza, non di etichetta.
            (1) Il nome è «Season Viewed» e non più «Season»: da quando la
            pagina porta in cima la barra di stato della stagione (doc 07
            §8.2), a schermo convivono DUE cose che parlano di annate — quella
            dichiara come sta il CLUB, questa dice che cosa sto guardando IO.
            Chiamarle tutt'e due «Season» le farebbe leggere come la stessa.
            (2) La voce «All seasons» non c'è più. Mescolava annate diverse in
            una lista sola e, soprattutto, nelle otto schede in alto: numeri
            che non erano di nessuna stagione e che nessuno poteva verificare.
            Il selettore mostra ora sempre e solo un'annata.

            ⚠️ Il campo resta vuoto in un caso solo — nessun anno noto né dal
            club né dai dati — e in quel caso non c'è comunque nessuna voce da
            offrire: non è un valore vuoto in mezzo a voci buone. */}
        <div className="qpsc-field">
          <span className="qpsc-field__label">Season Viewed</span>
          <select
            className="qpsc-select"
            aria-label="Season viewed"
            value={stagioneAttiva === null ? '' : String(stagioneAttiva)}
            onChange={(e) => setStagioneScelta(Number(e.target.value))}
          >
            {stagioniDisponibili.map((anno) => (
              <option key={anno} value={String(anno)}>{anno}</option>
            ))}
          </select>
        </div>
        <div className="qpsc-field">
          <span className="qpsc-field__label">Role</span>
          <select
            className="qpsc-select"
            aria-label="Filter by role"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
          {/* Voci = i cinque ruoli veri del modello nuovo (ROLE_ROUTES), con
              valori CANONICI maiuscoli (decisione del capomastro del 9/8,
              D-B).
              Le etichette leggibili sono una COPIA DELIBERATA di ROLE_LABELS
              (access.js): tenute a mano perché qui l'insieme e l'ORDINE
              delle voci sono una scelta di questa pagina, non il vocabolario
              — e perché una tendina non deve cambiare da sola il giorno in
              cui a ROLE_LABELS si aggiunge una riga. Il tutto-maiuscolo a
              schermo resta vietato fuori dalle pastiglie (doc 07 §3.2).
              Tolte tre voci morte: «Manager» — ruolo storico che non deve
              più comparire in alcuna tendina, e i cui documenti si trovano
              ora sotto «Coach» grazie al vocabolario, quindi non si perde
              nessuna riga; «Fan/Supporter» ed «eSport Member» — non
              registrabili (elenco chiuso del 6/8, server.js) e senza
              home nel modello nuovo.
              «Super Admin» è voce PROPRIA e non un sottoinsieme di Admin:
              il gate unico non ha gerarchia (cantiere U1-S), quindi il
              filtro «Admin» non mostrerebbe i superadmin e senza questa
              voce sarebbero introvabili con qualunque filtro di ruolo. */}
          <option value="">All Roles</option>
          <option value="PLAYER">Player</option>
          <option value="PARENT">Parent</option>
          <option value="COACH">Coach</option>
          <option value="ADMIN">Admin</option>
          <option value="SUPERADMIN">Super Admin</option>
          </select>
        </div>
        <div className="qpsc-field">
          <span className="qpsc-field__label">Team</span>
          <select
            className="qpsc-select"
            aria-label="Filter by team"
            value={filterTeam}
            onChange={(e) => setFilterTeam(e.target.value)}
          >
            <option value="">All Teams</option>
            {teamList.map((team, i) => (
              <option key={i} value={team}>{team}</option>
            ))}
          </select>
        </div>
        <div className="qpsc-field">
          <span className="qpsc-field__label">Balance</span>
          <select
            className="qpsc-select"
            aria-label="Filter by balance"
            value={filterBalance}
            onChange={(e) => setFilterBalance(e.target.value)}
          >
            <option value="all">All</option>
            <option value="positive">Balance Due</option>
            <option value="zero">Fully Paid</option>
          </select>
        </div>
        {/* §5.3 FANTASMA: Export CSV è un'azione di servizio, non l'azione
            principale della pagina — che qui non c'è. Stessa scelta di Club
            Finance per i suoi comandi di barra. A piena larghezza nella
            colonna stretta; sopra i 768px torna a misura (la variante
            `--wide` del foglio condiviso). Il comando non cambia: esporta
            `sortedUsers`, cioè quello che si sta guardando.
            ⚠️ D-ADMIN1 (RD-151): le colonne Role e Team del file sono le
            STESSE funzioni delle colonne a schermo, passate per nome. */}
        <button
          className="qpsc-ghost-btn qpsc-ghost-btn--wide"
          onClick={() =>
            exportFeesToCSV(sortedUsers, stagioneAttiva, {
              ruoli: getDisplayRoles,
              squadre: getTeamInfo
            })
          }
        >
          Export CSV
        </button>
      </div>

      {/* Il banner sta SOPRA la tabella, cioè in mezzo alla pagina fra i
          filtri e l'elenco: è dove cade l'occhio di chi ha appena aperto la
          pagina e non vede righe, e resta visibile a chi ha appena premuto
          una spunta senza vederla cambiare.
          ⚠️ La resa era in linea, col rosso `#b30000` che nella palette §1 non
          esiste; la grafica era rimandata a U1-G. È questa fetta: il banner
          prende una classe propria e il rosso LEGGIBILE del §1.3 (a 14px il
          Rosso QPSC non raggiunge AA). Quando compare, che cosa dice e da
          dove viene: invariati. */}
      {messaggio && (
        <p className="ul-msg">
          {messaggio}
        </p>
      )}

      <div className="ul-table-wrap">
      <table className="ul-table">
        <thead>
          <tr>
            <th></th>   {/* 👈 NUOVA COLONNA AVATAR */}

            {/* L'ordinamento per colonna è quello di sempre — stesso
                `handleSort`, stessa freccia: S5 riveste l'indicatore (oro,
                §1.2) e sposta nel foglio il segno che la colonna è toccabile,
                non tocca il comando. */}
            {/* MEMBER — le due colonne «First Name» e «Last Name» sono
                diventate UNA SOLA, come la colonna Member di Team Assignment:
                il nome di una persona è un'unità, e spezzarlo in due colonne
                costringeva a leggerlo in due tempi e a ordinarlo con due
                frecce. Da qui le colonne sono UNDICI, non dodici: chi tocca
                le regole posizionali del foglio (`:nth-child`) conti i `th`
                di questo `thead`, che è l'unica fonte vera.
                Una colonna sola, una chiave sola: `member`, che ordina per
                nome e a parità per cognome (vedi `sortedUsers`). */}
            <th className="ul-th--sortable" onClick={() => handleSort('member')}>
              Member {sortBy === 'member' && (
                <span className="ul-sort">{sortDir === 'asc' ? '▲' : '▼'}</span>
              )}
            </th>
            {/* §2 la categoria (BOYS/GIRLS/MEN/WOMEN) ha da qui una COLONNA
                PROPRIA, fra Member e Role. Prima stava dentro la cella del
                cognome, dove la pastiglia — che non si spezza mai
                (`.qpsc-pill` porta `white-space: nowrap`) — mandava a capo il
                cognome ogni volta che lo spazio si stringeva.
                L'intestazione è SCRITTA, non lasciata vuota come quelle di
                avatar e comandi: quelle due colonne non portano un dato ma
                un'immagine e due pulsanti, e non hanno un titolo da dare;
                questa porta un dato, e una colonna di dato dice il proprio
                nome. Non è ordinabile: per la categoria non esiste alcun
                `handleSort`, e non lo si inventa in una fetta di geometria. */}
            <th className="ul-cell--gender">Gender</th>
            <th className="ul-th--sortable" onClick={() => handleSort('role')}>
              Role {sortBy === 'role' && (
                <span className="ul-sort">{sortDir === 'asc' ? '▲' : '▼'}</span>
              )}
            </th>
            <th className="ul-th--sortable" onClick={() => handleSort('team')}>
              Team {sortBy === 'team' && (
                <span className="ul-sort">{sortDir === 'asc' ? '▲' : '▼'}</span>
              )}
            </th>
            <th className="ul-cell--center">Alert</th>
            <th className="ul-cell--center">PF</th>
            {/* Titoli corti: sopra la colonna basta il nome dell'importo.
                Il `data-label` delle celle resta per esteso («Total Due»,
                «Total Paid»), perché nella resa a card è l'unica etichetta
                che l'importo ha. */}
            <th className="ul-cell--num">Due</th>
            <th className="ul-cell--num">Paid</th>
            <th className="ul-cell--num">Balance</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sortedUsers.map((user) => {
            // ⚠️ PAY-SEASON F2: le tre colonne Due / Paid / Balance dicono
            // l'ANNATA GUARDATA, la stessa che governa la coorte, i filtri,
            // le card e il bottone «Payments» qui accanto. Prima dicevano il
            // campo piatto — cioè il 2026 — anche mentre la riga parlava di
            // un'altra annata.
            const quoteDiRiga = quoteDellAnnata(user, stagioneAttiva);
            const totalDue = quoteDiRiga.totalDue || 0;
            const totalPaid = totaleVersato(quoteDiRiga);
            const balance = totalDue - totalPaid;

            // Le due celle Role e Team invocavano ciascuna la propria funzione
            // in TRE punti (il primo valore, la lunghezza, il resto): fino a
            // sei costruzioni della stessa lista per ogni persona in elenco —
            // quattro nel caso ordinario, perché il terzo punto sta dentro il
            // ramo `length > 1` e su chi ha un solo ruolo (o una sola squadra)
            // non viene eseguito. Qui si calcolano una volta sola e si leggono
            // tre volte. Residuo lasciato aperto dalla chiusura di P7 per
            // getTeamInfo, esteso in P9 a getDisplayRoles, che aveva lo stesso
            // difetto nella cella accanto.
            //
            // ⚠️ NESSUN CAMBIO DI RESA, ed è verificabile: entrambe le funzioni
            // sono PURE (nessuno stato di React, nessuna scrittura, nessun
            // valore che cambi fra una chiamata e l'altra) e ricevono qui gli
            // stessi identici argomenti che ricevevano prima. Cambia solo
            // quante liste temporanee si costruiscono.
            //
            // ⚠️ L'asimmetria che P7 aveva lasciato a verbale — `getDisplayRoles`
            // scriveva «TEAMTD (…)» per qualunque context attivo con quel team
            // senza guardare il ruolo, mentre `getTeamInfo` un ruolo coach lo
            // pretendeva — NON è più: F5 ha tolto la spia da entrambe, e ora
            // le due colonne leggono la stessa cosa, la TESSERA.
            // ⚠️ UL-TESSERE: le due liste si leggono ora sull'annata GUARDATA,
            // la stessa che governa la coorte, le card e i filtri. Gli
            // argomenti restano di sola lettura, e le due funzioni restano
            // pure: ricevono l'anno per parametro, come già fanno
            // `categoriaAnagrafica` qui sotto e la coorte più in alto.
            const righeRuoli = getDisplayRoles(user, stagioneAttiva);
            const righeSquadre = getTeamInfo(user, stagioneAttiva);

            // ⚠️ PFA: i due dati per-stagione della riga, letti UNA VOLTA
            // sull'annata guardata dalle sedi uniche di pagina. L'alert serve
            // a quattro punti (nome, cognome e i due rami del pallino) e Play
            // Football a due (le due spunte): leggerli qui una volta sola è
            // ciò che impedisce a un segno di divergere dal comando che gli
            // sta accanto.
            const alertDellAnnata = haAlertNellAnnata(user, stagioneAttiva);
            const pfDellAnnata = playFootballDellAnnata(user, stagioneAttiva);

            return (
              // Evidenza dell'iscritto in attesa (P8), resa MINIMA come
              // prescrive il doc 07 §8.1: la riga porta filetto ambra e
              // tinta, e basta. NESSUNA pastiglia di stato in riga, nessuna
              // colonna Status nuova, nessuna scritta «PENDING» ripetuta —
              // è la revisione di collaudo del 5/8/2026, e vale per ogni
              // stato e per entrambe le rese. ARCHIVED non si rende affatto:
              // con il criterio qui sopra quelle righe non entrano in lista,
              // quindi la resa rossa al 50% del doc 07 §8.1 qui non ha
              // materia (nessuna deroga da registrare). Lo stato IGNOTO non
              // si rende: non si dipinge ciò che non si sa leggere.
              // ⚠️ La riga «da completare» (ESEC-USERLIST-INCOMPLETE): una
              // persona ACTIVE che deve ancora completare la stagione porta
              // la stessa evidenza ambra di Team Assignment — SOLO il colore,
              // nessuna label: qui il colore basta, l'azione resta in Team
              // Assignment (decisione del committente). Il flag arriva già
              // dal backend su ogni riga (`GET /users`), che lo spegne da sé
              // per ARCHIVED e PENDING. La condizione è scritta DIFENSIVA —
              // PENDING vince comunque — per non dipendere dall'invariante di
              // un altro file: gli iscritti PENDING sono in elenco davvero
              // (il filtro della base toglie i soli ARCHIVED, e le due tinte
              // non devono mai sovrapporsi su una riga sola).
              // ⚠️ E l'evidenza è legata all'ANNATA GUARDATA, come in Team
              // Assignment: `mustCompleteSeason` parla dell'anno di ENROLLMENT
              // e di quello solo, quindi la tinta si accende SOLO mentre si
              // guarda quell'anno (`guardoAnnoIscrizione`, in testa al
              // componente). Su un'altra annata la riga direbbe il falso — il
              // rinnovante finiva marcato sul 2026 per una mancanza del 2027.
              // ⚠️ «USER LIST PER ANNO GUARDATO»: l'ambra «in attesa» si
              // accende sullo STATO DELL'ANNO GUARDATO (`statoDellAnno`), non
              // più sullo `status` della persona — è la stessa fonte della
              // fila ambra di Team Assignment. Il rinnovato con tessera 2027
              // PENDING è ambra sul 2027 e non lo era; il nuovo iscritto con
              // la sola tessera PENDING ora compare, ed è ambra. La riga «da
              // completare» resta com'è: PENDING vince ancora su di lei.
              <tr
                key={user.id}
                className={
                  statoDellAnno(user) === "PENDING"
                    ? "ul-pending"
                    : (guardoAnnoIscrizione && user.mustCompleteSeason === true ? "ul-incomplete" : undefined)
                }
              >
                <td data-label="Avatar" className="ul-cell--center">
                  {/* Foto dal backend con fetch autenticata: lo Storage e'
                      chiuso in lettura (storage.rules). Vedi AuthAvatar.
                      La misura e il bordo (oro leggero, §1.2) stanno ora nel
                      foglio: erano in linea, col `#d4b06a` abolito dal §1.4.
                      La classe raggiunge anche il ripiego a iniziali, che
                      AuthAvatar disegna con lo stesso `className`. */}
                  <AuthAvatar
                    userId={user.id}
                    photoKey={user.photoUrl || ""}
                    name={`${user.firstName || ""} ${user.lastName || ""}`}
                    alt="avatar"
                    fallbackSrc={avatarPlaceholder}
                    className="ul-avatar"
                  />
                </td>

                {/* §8.1 deroga (i) al §1.5: la persona marcata dall'admin si
                    dice in rosso leggibile. Il colore non cambia; cambia la
                    domanda.
                    ⚠️ PFA: l'alert è PER STAGIONE e sta sulla persona, non in
                    tessera. La condizione non è più «questa persona è marcata»
                    ma «è marcata NELL'ANNATA CHE STIAMO GUARDANDO», letta
                    dalla sede unica di pagina (`haAlertNellAnnata`). Chi ha un
                    alert 2026 non lo porta più con sé nel 2027. */}
                {/* MEMBER — una cella sola per nome e cognome, resi come li
                    rende Team Assignment: «Nome Cognome», uno spazio in mezzo.
                    ⚠️ CONSEGUENZA DELLA FUSIONE, messa a verbale: l'evidenza
                    rossa dell'annata stava su DUE celle (nome e cognome) e da
                    qui sta su UNA. Non è una scelta nuova di colore — è che il
                    nome, ora un'unità, diventa rosso in un colpo solo.
                    ⚠️ Niente `.ul-stack` qui: quell'impianto serve alla futura
                    riga famiglia, che è un cantiere a sé. Per ora la cella
                    porta il solo nome, reso come lo rendeva prima. */}
                <td
                  data-label="Member"
                  className={alertDellAnnata ? "ul-cell--alert" : undefined}
                >
                  {user.firstName} {user.lastName}
                </td>

                {/* §2 la pastiglia della categoria, ora in colonna propria, e
                    TACE se genere o data di nascita non bastano a dirla (doc 07
                    §8.1, aggiunta 26/8/2026): `PastigliaCategoriaSeNota`
                    restituisce `null`, e la cella resta VUOTA. Vuota, non
                    assente: una `<td>` in meno slitterebbe di un posto tutte le
                    colonne che seguono. Che sotto i 768px una cella vuota non
                    lasci una riga «Gender:» senza risposta lo risolve il foglio
                    (`.ul-cell--gender:empty`), non un ramo in più qui.
                    L'annata su cui si misura la categoria è quella che la
                    pagina sta GUARDANDO: l'età è quella compiuta al 31 dicembre
                    dell'annata, non alla data di oggi — altrimenti la pastiglia
                    di un socio passerebbe da BOYS a MEN a stagione in corso, il
                    giorno del suo compleanno. Se l'annata non si sa (`null`) il
                    calcolo ripiega da sé sull'oggi (utils/categoria.js):
                    nessuno inventa un anno. */}
                <td data-label="Gender" className="ul-cell--gender">
                  <PastigliaCategoriaSeNota
                    category={categoriaAnagrafica(user, stagioneAttiva)}
                  />
                </td>

                {/* Il sottotesto (i ruoli e le squadre oltre al primo) passa
                    al testo secondario: erano un blu `#4da3ff` e un rosso
                    `#d21f25` fuori palette, che per giunta coloravano un dato
                    senza esserne autorizzati (§1.5). Che cosa mostrano, e in
                    quale ordine, non cambia. */}
                <td data-label="Role">
                  <div className="ul-stack">
                    <div className="ul-stack__main">
                      {righeRuoli[0]}
                    </div>

                    {righeRuoli.length > 1 && (
                      <>
                        <div className="ul-stack__sep" />
                        <div className="ul-stack__more">
                          {righeRuoli.slice(1).join(" · ")}
                        </div>
                      </>
                    )}
                  </div>
                </td>

                <td data-label="Team">
                  <div className="ul-stack">
                    <div className="ul-stack__main">
                      {righeSquadre[0]}
                    </div>

                    {righeSquadre.length > 1 && (
                      <>
                        <div className="ul-stack__sep" />
                        <div className="ul-stack__more">
                          {righeSquadre.slice(1).join(" · ")}
                        </div>
                      </>
                    )}
                  </div>
                </td>

                <td data-label="Alert" className="ul-cell--center">
                  {/* Stesso criterio del comando (`puoAmministrare`): prima
                      l'espressione era ricopiata qui, e una copia può
                      divergere da ciò che `toggleAlert` consente davvero.
                      §8.1 deroga (i) al §1.5: il pallino dice uno STATO —
                      verde di stato, Rosso QPSC quando la persona è marcata.
                      I due rami restano due.
                      ⚠️ PFA: la condizione è ora quella dell'ANNATA GUARDATA
                      (`alertDellAnnata`, calcolata in cima alla riga), la
                      stessa che `toggleAlert` accende e spegne. Il pallino e
                      il comando leggono lo stesso valore, che è il motivo per
                      cui il valore si legge una volta sola. */}
                  {puoAmministrare ? (
                    <span
                      className={`ul-dot ul-dot--action${alertDellAnnata ? " ul-dot--on" : ""}`}
                      onClick={() => toggleAlert(user)}
                      title="Toggle Alert"
                    >
                      ●
                    </span>
                  ) : (
                    <span
                      className={`ul-dot${alertDellAnnata ? " ul-dot--on" : ""}`}
                    >
                      ●
                    </span>
                  )}
                </td>

                <td data-label="PF" className="ul-cell--center">
                  {/* Idoneità Play Football: sede unica (utils/playFootball.js),
                      la STESSA che conta la card in alto. L'elenco letterale
                      che stava qui — ["Player","Coach","Manager","TD"] — era
                      una seconda definizione della stessa domanda, e per
                      giunta diversa da quella della card: è il difetto U1-A
                      della doppia idoneità (rapporto 11 §8.1), chiuso qui. */}
                  {/* §8.1 deroga (ii) al §1.5: spunta ottenuta in verde di
                      stato, mancante in testo secondario — mai un rosso: non
                      è un errore, è un adempimento che non c'è ancora. Le
                      condizioni (e il `togglePF` che sta sotto) sono quelle
                      di prima: `puoAmministrare` decide il solo puntatore,
                      come faceva lo stile in linea. */}
                  {/* ⚠️ UL-TESSERE: sulla persona proiettata sull'annata, come
                      la card in alto. Un giocatore di un'altra annata non ha
                      spunte Play Football in questa. */}
                  {/* ⚠️ PFA: e ora anche i VALORI sono dell'annata guardata
                      (`pfDellAnnata`, in cima alla riga), non più i due
                      booleani senz'anno. Prima l'idoneità cambiava con «Season
                      Viewed» e le spunte no: la colonna poteva comparire e
                      sparire, ma i due segni restavano identici in ogni
                      stagione. L'IDONEITÀ NON È TOCCATA — resta PLAYER/COACH
                      nella sua sede unica (utils/playFootball.js), quindi un
                      amministratore puro continua a non avere spunte. */}
                  {isPlayFootballEligible(personaDellAnnata(user, stagioneAttiva)) ? (
                    <>
                      <div
                        className={`ul-pf${pfDellAnnata.registered ? " ul-pf--on" : ""}${puoAmministrare ? " ul-pf--action" : ""}`}
                        title="Play Football Registration"
                        onClick={() => togglePF(user, "registered")}
                      >
                        ✔
                      </div>
                      <div
                        className={`ul-pf${pfDellAnnata.feePaidByClub ? " ul-pf--on" : ""}${puoAmministrare ? " ul-pf--action" : ""}`}
                        title="Play Football Fee Paid by QPSC"
                        onClick={() => togglePF(user, "feePaidByClub")}
                      >
                        ✔
                      </div>
                    </>
                  ) : (
                    <span className="ul-none">—</span>
                  )}
                </td>

                {/* Colonne pagamenti + bottone «Payments»: unico ternario,
                    criterio unico (`haQuota`, in testa al file). Qui muore
                    il reperto dell'8/8: la riga "Player" mostrava colonne e
                    bottone, le righe "PLAYER" la cella «N/A». */}
                {haQuota(user, stagioneAttiva) ? (
                  <>
                    <td data-label="Total Due" className="ul-cell--num">{denaroAschermo(totalDue)}</td>
                    <td data-label="Total Paid" className="ul-cell--num">{denaroAschermo(totalPaid)}</td>
                    {/* §8.1 deroga (iii) al §1.5: il saldo si colora per
                        segno, come in Club Finance. Il confronto è quello di
                        prima (`balance > 0`). */}
                    <td
                      data-label="Balance"
                      className={`ul-cell--num ${balance > 0 ? 'ul-amount--due' : 'ul-amount--paid'}`}
                    >
                      {denaroAschermo(balance)}
                    </td>
                    {/* §5.3 fantasma per Payments (azione di servizio) e §5.4
                        per View Profile, la classe condivisa di Team
                        Assignment. Che cosa fanno: esattamente quello che
                        facevano. A piena larghezza nella card, a misura e in
                        riga sopra i 768px. */}
                    <td data-label="">
                      <div className="ul-actions">
                        <button
                          className="qpsc-ghost-btn qpsc-ghost-btn--wide"
                          onClick={() => setSelectedUser(user)}
                        >
                          Payments
                        </button>
                        <button
                          className="qpsc-profile-btn qpsc-profile-btn--wide"
                          onClick={() => navigate(`/profile/${user.id}`)}
                        >
                          View Profile
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td data-label="Total Due" colSpan={3} className="ul-cell--na">
                      N/A
                    </td>
                    <td data-label="">
                      <div className="ul-actions">
                        <button
                          className="qpsc-profile-btn qpsc-profile-btn--wide"
                          onClick={() => navigate(`/profile/${user.id}`)}
                        >
                          View Profile
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {/* MODAL per pagamenti */}
      {selectedUser && (
        <UserPaymentsModal
          user={selectedUser}
          // ⚠️ PAY-SEASON F2: l'annata GUARDATA scende nella finestra, che
          // su quella legge e su quella scrive. Non la ricalcola per conto
          // suo: una finestra con un'idea propria dell'anno potrebbe mostrare
          // (o peggio, scrivere) un'annata diversa da quella dell'elenco che
          // le sta dietro.
          season={stagioneAttiva}
          onClose={() => setSelectedUser(null)}
          onUpdate={handleUpdateFees}
        />
      )}
    </HomeShell>
  );
};

export default UsersList;